import { useState, useEffect, useRef, useCallback } from "react";
import Footer from "./Footer";
import DashboardMockup from "../components/DashboardMockup";
import SamplePassport from "../components/SamplePassport"; // adjust path if the file lives elsewhere

// ── Google Apps Script Web App URL (the NEW Acespire Gmail account's script) ──
// Paste the /exec URL you get after deploying Code_handbook_and_expert.gs.
// `type` decides what the script does (email the handbook / store the expert form).
const FORM_SHEET_URL = "https://script.google.com/macros/s/AKfycbzZn3mPLQ8O1IFlh_G5gy0fz-fM06367XcQbPYlM40iXNcE0vWkH7cdgytrnEmuI13J/exec";

const submitToSheet = (data) => {
  const params = new URLSearchParams({ ...data, timestamp: new Date().toISOString() });
  return fetch(`${FORM_SHEET_URL}?${params.toString()}`, { method: "GET", mode: "no-cors" });
};

/* ─────────────────────────────────────────────────────────────────────────────
   CSS - injected as <style id="bp-styles">, removed on unmount.
   Uses Inter to match the rest of the Provinyx site.
   Animations: scroll-reveal, floating hero card, hover lifts, stagger.
───────────────────────────────────────────────────────────────────────────── */
const PAGE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

:root{
  --bg:#F6F8FC;--surface:#FFFFFF;--tint:#EDF2FB;--ink:#0E1729;--muted:#56627A;--line:#D9E1EE;
  --accent:#2563EB;--accent-strong:#1D4ED8;--accent-soft:#E3ECFE;--on-accent:#FFFFFF;
  --good:#0F8A5F;--good-soft:#DDF4EA;--amber:#A8650B;--amber-soft:#FCEFD9;
  --violet:#6D3FD1;--violet-soft:#ECE5FC;
  --deep:#0B1430;--deep-ink:#EAF0FF;--deep-muted:#A9B6D6;
  --f-sans:"Inter",system-ui,-apple-system,"Segoe UI",sans-serif;
  --f-mono:ui-monospace,"SF Mono","Cascadia Code",Menlo,monospace;
  --r:14px;--r-sm:8px;
  --shadow:0 1px 2px rgba(14,23,41,.06),0 12px 32px -12px rgba(14,23,41,.18);
  --shadow-lift:0 4px 6px rgba(14,23,41,.07),0 20px 40px -12px rgba(14,23,41,.22);
}
@media(prefers-color-scheme:dark){:root:not([data-theme="light"]){
  --bg:#0A101E;--surface:#111A2D;--tint:#0E1628;--ink:#E7EDF8;--muted:#9AA7C0;--line:#24314A;
  --accent:#5B8DF6;--accent-strong:#7FA6FA;--accent-soft:#16264A;--on-accent:#06102A;
  --good:#3CC48F;--good-soft:#0F2E24;--amber:#E9A84A;--amber-soft:#33240C;
  --violet:#A98BF5;--violet-soft:#241A40;
  --deep:#060B18;--deep-ink:#EAF0FF;--deep-muted:#A9B6D6;
  --shadow:0 1px 2px rgba(0,0,0,.4),0 12px 32px -12px rgba(0,0,0,.6);color-scheme:dark;}}
:root[data-theme="dark"]{
  --bg:#0A101E;--surface:#111A2D;--tint:#0E1628;--ink:#E7EDF8;--muted:#9AA7C0;--line:#24314A;
  --accent:#5B8DF6;--accent-strong:#7FA6FA;--accent-soft:#16264A;--on-accent:#06102A;
  --good:#3CC48F;--good-soft:#0F2E24;--amber:#E9A84A;--amber-soft:#33240C;
  --violet:#A98BF5;--violet-soft:#241A40;
  --deep:#060B18;--deep-ink:#EAF0FF;--deep-muted:#A9B6D6;
  --shadow:0 1px 2px rgba(0,0,0,.4),0 12px 32px -12px rgba(0,0,0,.6);color-scheme:dark;}

/* ── Keyframes ─────────────────────────────────────────────────────────────── */
@keyframes bp-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
@keyframes bp-pulse-blob{0%,100%{opacity:.18;transform:scale(1)}50%{opacity:.28;transform:scale(1.12)}}
@keyframes bp-pulse-blob2{0%,100%{opacity:.12;transform:scale(1)}50%{opacity:.22;transform:scale(1.08)}}
@keyframes bp-fade-up{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
@keyframes bp-slide-left{from{opacity:0;transform:translateX(-40px)}to{opacity:1;transform:translateX(0)}}
@keyframes bp-slide-right{from{opacity:0;transform:translateX(40px)}to{opacity:1;transform:translateX(0)}}
@keyframes bp-scale-in{from{opacity:0;transform:scale(.92)}to{opacity:1;transform:scale(1)}}
@keyframes bp-spin-slow{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
@keyframes bp-shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}
@keyframes bp-bar-grow{from{width:0}to{width:98%}}
@keyframes bp-pulse-dot{0%,100%{opacity:1}50%{opacity:.35}}
@keyframes bp-dot-pop{0%,100%{transform:scale(1)}50%{transform:scale(1.6)}}

/* ── Scroll-reveal system ──────────────────────────────────────────────────── */
.bp-rv{opacity:0;transform:translateY(28px);transition:opacity .65s cubic-bezier(.16,1,.3,1),transform .65s cubic-bezier(.16,1,.3,1)}
.bp-rv.on{opacity:1;transform:translateY(0)}
.bp-rv-l{opacity:0;transform:translateX(-38px);transition:opacity .65s cubic-bezier(.16,1,.3,1),transform .65s cubic-bezier(.16,1,.3,1)}
.bp-rv-l.on{opacity:1;transform:translateX(0)}
.bp-rv-r{opacity:0;transform:translateX(38px);transition:opacity .65s cubic-bezier(.16,1,.3,1),transform .65s cubic-bezier(.16,1,.3,1)}
.bp-rv-r.on{opacity:1;transform:translateX(0)}
.bp-rv-s{opacity:0;transform:scale(.93);transition:opacity .55s cubic-bezier(.16,1,.3,1),transform .55s cubic-bezier(.16,1,.3,1)}
.bp-rv-s.on{opacity:1;transform:scale(1)}
.bp-d1{transition-delay:.08s}.bp-d2{transition-delay:.16s}.bp-d3{transition-delay:.24s}
.bp-d4{transition-delay:.32s}.bp-d5{transition-delay:.40s}.bp-d6{transition-delay:.48s}

/* ── Base ──────────────────────────────────────────────────────────────────── */
.bp-page *{box-sizing:border-box}
.bp-page{
  background:var(--bg);color:var(--ink);
  font-family:var(--f-sans);font-size:16px;line-height:1.6;
  -webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;
}
.bp-page h1,.bp-page h2,.bp-page h3{
  font-family:var(--f-sans);line-height:1.15;margin:0;
  letter-spacing:-.02em;font-weight:800;
}
.bp-page h1{font-size:clamp(2rem,4.5vw,3.4rem)}
.bp-page h2{font-size:clamp(1.55rem,3vw,2.3rem)}
.bp-page h3{font-size:1.08rem;font-weight:700;letter-spacing:-.01em}
.bp-page p{margin:0}
.bp-page a{color:var(--accent)}
.bp-wrap{max-width:1160px;margin-inline:auto;padding-inline:clamp(16px,5vw,32px)}
.bp-page [id]{scroll-margin-top:84px}
.bp-page section{padding-block:clamp(52px,8vw,96px)}
.bp-band{background:var(--tint)}
.bp-eyebrow{
  font-family:var(--f-sans);font-size:.74rem;letter-spacing:.1em;
  text-transform:uppercase;color:var(--accent);font-weight:700;
}
.bp-lede{color:var(--muted);font-size:1.05rem;max-width:58ch;line-height:1.7}
.bp-head{display:grid;gap:14px;margin-bottom:40px;max-width:720px}
.bp-btns{display:flex;flex-wrap:wrap;gap:12px}

/* Buttons */
.bp-btn{
  display:inline-flex;align-items:center;gap:8px;
  padding:13px 22px;border-radius:999px;font-weight:600;font-size:.95rem;
  text-decoration:none;border:1.5px solid transparent;cursor:pointer;
  font-family:var(--f-sans);background:none;
  transition:background .2s,border-color .2s,transform .18s,box-shadow .2s;
  will-change:transform;
}
.bp-btn:hover{transform:translateY(-2px)}
.bp-btn:active{transform:translateY(0)}
.bp-btn-primary{background:var(--accent);color:var(--on-accent);border-color:var(--accent);box-shadow:0 4px 14px rgba(37,99,235,.35)}
.bp-btn-primary:hover{background:var(--accent-strong);border-color:var(--accent-strong);box-shadow:0 8px 22px rgba(37,99,235,.45)}
.bp-btn-ghost{border-color:var(--accent);color:var(--ink);background:var(--surface)}
.bp-btn-ghost:hover{border-color:var(--accent);color:var(--accent)}
.bp-link-arrow{font-weight:600;text-decoration:none;display:inline-flex;gap:6px;align-items:center;color:var(--accent);background:none;border:none;cursor:pointer;font-size:.95rem;font-family:var(--f-sans);transition:gap .2s}
.bp-link-arrow:hover{gap:10px}

/* Chips / cards */
.bp-chip{display:inline-flex;align-items:center;gap:6px;padding:6px 12px;border-radius:999px;font-size:.86rem;background:var(--surface);border:1px solid var(--line);white-space:nowrap}
.bp-chips{display:flex;flex-wrap:wrap;gap:8px}
.bp-card{
  background:var(--surface);border:1px solid var(--line);border-radius:var(--r);
  transition:transform .22s cubic-bezier(.16,1,.3,1),box-shadow .22s;
}
.bp-card:hover{transform:translateY(-3px);box-shadow:var(--shadow-lift)}
.bp-small{font-size:.86rem;color:var(--muted)}

/* ── Hero ──────────────────────────────────────────────────────────────────── */
.bp-hero{
  padding-block:clamp(88px,8vw,96px) clamp(48px,7vw,88px); /* top clears the fixed navbar */
  position:relative;overflow:hidden;
}
.bp-hero-bg{position:absolute;inset:0;pointer-events:none;overflow:hidden;z-index:0}
.bp-blob1{
  position:absolute;top:-60px;left:-80px;
  width:clamp(280px,40vw,520px);height:clamp(280px,40vw,520px);
  border-radius:50%;background:radial-gradient(circle,var(--accent-soft),transparent 70%);
  animation:bp-pulse-blob 7s ease-in-out infinite;
}
.bp-blob2{
  position:absolute;bottom:-80px;right:-60px;
  width:clamp(200px,30vw,400px);height:clamp(200px,30vw,400px);
  border-radius:50%;background:radial-gradient(circle,#dbeafe,transparent 70%);
  animation:bp-pulse-blob2 9s ease-in-out infinite 1.5s;
}
.bp-hero-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:clamp(24px,4vw,48px);align-items:center;position:relative;z-index:1}
.bp-hero-copy{display:grid;gap:22px;min-width:0}
.bp-hero-badge{
  display:inline-flex;align-items:center;gap:8px;
  padding:7px 14px;border-radius:999px;font-size:.8rem;font-weight:700;
  letter-spacing:.06em;text-transform:uppercase;
  background:var(--accent-soft);color:var(--accent);border:1px solid rgba(37,99,235,.2);
  width:fit-content;
}
.bp-hero-visual{position:relative;display:grid;place-items:center;min-height:380px}
@media(min-width:1200px){.bp-hero-visual{margin-right:-40px}}
.bp-hero-visual .glow{
  position:absolute;inset:5% 5%;border-radius:50%;
  background:radial-gradient(closest-side,var(--accent-soft),transparent);
  animation:bp-pulse-blob 5s ease-in-out infinite;
}
.bp-passport-float{animation:bp-float 4.5s ease-in-out infinite}
.bp-trust{display:flex;flex-wrap:wrap;gap:16px;color:var(--muted);font-size:.86rem}
.bp-trust span{display:inline-flex;gap:6px;align-items:center;font-weight:500}
.bp-trust svg{color:var(--good);flex-shrink:0}
@media(max-width:860px){
  .bp-hero-grid{grid-template-columns:1fr}
  .bp-hero-visual{min-height:260px}
}
@media(max-width:480px){
  .bp-hero-visual{min-height:200px}
  .bp-page h1{font-size:1.85rem}
}

/* passport card */
.passport{
  background:var(--surface);border:1px solid var(--line);border-radius:var(--r);
  box-shadow:var(--shadow);padding:16px;display:grid;gap:12px;font-size:.84rem;
}
.pp-top{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}
.pp-top .t{font-family:var(--f-sans);font-weight:700;font-size:.98rem;letter-spacing:-.01em}
.pp-id{font-family:var(--f-mono);font-size:.72rem;color:var(--muted)}
.pp-qr{width:58px;height:58px;flex:none;color:var(--ink)}
.pp-rows{display:grid;gap:6px}
.pp-row{display:flex;justify-content:space-between;gap:10px;border-top:1px solid var(--line);padding-top:6px}
.pp-row span:first-child{color:var(--muted)}
.pp-row b{font-weight:600;font-variant-numeric:tabular-nums}
.bar{height:6px;background:var(--tint);border-radius:999px;overflow:hidden}
.bar i{display:block;height:100%;background:var(--good);border-radius:inherit;animation:bp-bar-grow .8s ease .4s both}
.cls{display:inline-grid;place-items:center;width:22px;height:22px;border-radius:5px;background:var(--good);color:var(--surface);font-weight:700;font-size:.78rem}

/* ── Timeline ──────────────────────────────────────────────────────────────── */
.bp-timeline{
  margin-top:40px;display:grid;grid-template-columns:repeat(3,1fr);
  position:relative;border-top:2px solid var(--line);padding-top:20px;gap:16px;
}
.bp-tl{display:grid;gap:4px;position:relative}
.bp-tl::before{
  content:"";position:absolute;top:-28px;left:0;width:14px;height:14px;
  border-radius:50%;background:var(--surface);border:2px solid var(--line);
  transition:transform .3s ease;
}
.bp-tl:hover::before{transform:scale(1.3)}
.bp-tl.past::before{background:var(--good);border-color:var(--good)}
.bp-tl.key::before{
  background:var(--accent);border-color:var(--accent);
  box-shadow:0 0 0 5px var(--accent-soft);
  animation:bp-dot-pop 2.5s ease-in-out infinite;
}
.bp-tl .d{font-family:var(--f-mono);font-size:.78rem;color:var(--muted)}
.bp-tl b{font-weight:700}
.bp-tl .verify{font-family:var(--f-mono);font-size:.68rem;color:var(--amber)}
@media(max-width:560px){
  .bp-timeline{
    grid-template-columns:1fr;border-top:0;
    border-left:2px solid var(--line);padding-top:0;padding-left:20px;gap:22px;
  }
  .bp-tl::before{top:4px;left:-28px}
}

/* ── Contents / tabs ───────────────────────────────────────────────────────── */
.bp-legend{display:flex;gap:16px;flex-wrap:wrap;font-size:.86rem;color:var(--muted);margin-bottom:18px}
.bp-legend span{display:inline-flex;gap:7px;align-items:center;font-weight:500}
.bp-contents{display:grid;grid-template-columns:220px 1fr;gap:0;overflow:hidden}
.bp-ctabs{display:grid;align-content:start;border-right:1px solid var(--line);padding:10px}
.bp-ctab{
  all:unset;cursor:pointer;display:flex;gap:10px;align-items:center;
  padding:11px 13px;border-radius:var(--r-sm);font-weight:500;font-size:.93rem;
  transition:background .15s,color .15s,transform .15s;
}
.bp-ctab .n{font-family:var(--f-mono);font-size:.72rem;color:var(--muted);width:18px}
.bp-ctab:hover{background:var(--tint);transform:translateX(2px)}
.bp-ctab.active{background:var(--accent-soft);color:var(--accent-strong);font-weight:600}
.bp-ctab.active .n{color:var(--accent-strong)}
.bp-cpanel{padding:28px;display:grid;gap:16px;align-content:start;min-width:0;transition:opacity .2s}
.bp-cpanel p{color:var(--muted);max-width:60ch}
.bp-field{
  display:inline-flex;align-items:center;gap:8px;padding:8px 12px;
  border-radius:8px;font-size:.88rem;background:var(--tint);border:1px solid var(--line);
  transition:transform .15s,box-shadow .15s;
}
.bp-field:hover{transform:translateY(-1px);box-shadow:0 3px 8px rgba(37,99,235,.12)}
.bp-field i{width:8px;height:8px;border-radius:2px;flex:none}
.bp-field.rep{background:var(--amber-soft);border-color:transparent}
.bp-field.auth{background:var(--violet-soft);border-color:transparent}
.bp-field.live{border-style:dashed;border-color:var(--good)}
@media(max-width:760px){
  .bp-contents{grid-template-columns:1fr}
  .bp-ctabs{border-right:0;border-bottom:1px solid var(--line);grid-template-columns:repeat(auto-fill,minmax(140px,1fr))}
}
@media(max-width:420px){
  .bp-ctabs{grid-template-columns:1fr 1fr}
}

/* ── Scope ─────────────────────────────────────────────────────────────────── */
.bp-scope{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
.bp-scope .bp-card{padding:22px;display:grid;gap:10px;align-content:start}
.bp-scope .bp-card svg{color:var(--accent)}
.bp-scope .bp-card span{color:var(--muted);font-size:.92rem}
.bp-scope-foot{display:grid;grid-template-columns:1fr 1.4fr;gap:16px;margin-top:16px}
.bp-scope-foot .bp-card{padding:20px 22px;display:grid;gap:10px;align-content:start}
.bp-out{display:flex;gap:10px;align-items:flex-start}
.bp-out svg{color:var(--muted);flex:none;margin-top:3px}
.bp-roles{display:grid;gap:8px}
.bp-roles div{display:grid;grid-template-columns:110px 1fr;gap:12px;font-size:.92rem;align-items:baseline}
.bp-roles b{font-weight:600}
.bp-roles span{color:var(--muted)}
@media(max-width:860px){.bp-scope{grid-template-columns:1fr 1fr}}
@media(max-width:560px){.bp-scope{grid-template-columns:1fr}.bp-scope-foot{grid-template-columns:1fr}}
@media(max-width:420px){.bp-roles div{grid-template-columns:1fr;gap:2px}}

/* ── Flow ──────────────────────────────────────────────────────────────────── */
.bp-flow{display:grid;grid-template-columns:repeat(6,1fr);gap:0}
.bp-fnode{display:grid;gap:10px;align-content:start;padding:0 12px 0 0;position:relative}
.bp-fnode .box{
  display:grid;place-items:center;height:72px;border-radius:var(--r);
  background:var(--surface);border:1px solid var(--line);color:var(--accent);
  position:relative;transition:transform .22s cubic-bezier(.16,1,.3,1),box-shadow .22s,background .22s;
}
.bp-fnode:hover .box{transform:translateY(-6px);box-shadow:0 12px 28px -8px rgba(37,99,235,.28)}
.bp-fnode.core .box{background:var(--accent);color:var(--on-accent);border-color:var(--accent)}
.bp-fnode.core:hover .box{background:var(--accent-strong)}
.bp-fnode:not(:last-child) .box::after{
  content:"";position:absolute;right:-13px;top:50%;width:14px;height:2px;background:var(--line);
}
.bp-fnode b{font-size:.92rem;font-weight:600}
.bp-fnode span{color:var(--muted);font-size:.84rem}
@media(max-width:960px){
  .bp-flow{grid-template-columns:repeat(3,1fr);row-gap:28px}
  .bp-fnode:nth-child(3) .box::after{display:none}
  .bp-fnode:nth-child(6) .box::after{display:none}
}
@media(max-width:520px){
  .bp-flow{grid-template-columns:1fr 1fr}
  .bp-fnode .box::after{display:none}
}
@media(max-width:360px){
  .bp-flow{grid-template-columns:1fr}
}

/* ── Gap quiz ──────────────────────────────────────────────────────────────── */
.bp-gap-wrap{display:grid;grid-template-columns:1fr 1fr;gap:40px;align-items:start}
.bp-quiz{padding:22px;display:grid;gap:14px;position:relative;overflow:hidden}
.bp-q{display:grid;gap:10px;padding-bottom:14px;border-bottom:1px solid var(--line)}
.bp-q p{font-weight:600;margin:0}
.bp-q .opts{display:flex;gap:8px;flex-wrap:wrap}
.bp-opt{
  all:unset;cursor:pointer;padding:7px 16px;border:1.5px solid var(--line);
  border-radius:999px;font-size:.86rem;background:var(--surface);font-family:var(--f-sans);
  transition:all .15s ease;
}
.bp-opt:hover{border-color:var(--accent);transform:translateY(-1px)}
.bp-opt.sel{background:var(--accent);border-color:var(--accent);color:var(--on-accent);box-shadow:0 4px 12px rgba(37,99,235,.3)}
.bp-locked{display:grid;gap:10px;filter:blur(4px);user-select:none;opacity:.7;pointer-events:none}
.bp-locked div{padding:10px 0;border-bottom:1px solid var(--line)}
.bp-unlock{
  position:absolute;left:0;right:0;bottom:0;height:62%;
  display:grid;place-items:center;align-content:center;gap:12px;
  background:linear-gradient(to bottom,transparent,var(--surface) 38%);
  text-align:center;padding:20px;
}
.bp-score-now{display:flex;justify-content:space-between;align-items:baseline;font-family:var(--f-mono);font-size:.8rem;color:var(--muted)}
.bp-score-now b{font-family:var(--f-sans);font-size:1.4rem;color:var(--ink);font-variant-numeric:tabular-nums;font-weight:800}
.bp-steps{display:grid;gap:0;margin-top:8px}
.bp-step{
  display:grid;grid-template-columns:36px 1fr;gap:4px 16px;
  padding:16px 0;border-top:1px solid var(--line);
  transition:background .15s;border-radius:4px;
}
.bp-step:hover{background:var(--tint)}
.bp-step .k{
  grid-row:span 2;width:30px;height:30px;border-radius:50%;
  display:grid;place-items:center;
  background:var(--accent-soft);color:var(--accent-strong);
  font-family:var(--f-mono);font-size:.78rem;font-weight:600;
  transition:background .2s,color .2s,transform .2s;
}
.bp-step:hover .k{background:var(--accent);color:var(--on-accent);transform:scale(1.1)}
.bp-step b{font-weight:700}
.bp-step .io{display:grid;grid-template-columns:1fr 1fr;gap:12px;font-size:.88rem;color:var(--muted)}
.bp-step .io em{font-style:normal;font-family:var(--f-mono);font-size:.66rem;text-transform:uppercase;letter-spacing:.06em;display:block;color:var(--muted);opacity:.8}
.bp-step .io span:last-child{color:var(--ink)}
@media(max-width:900px){.bp-gap-wrap{grid-template-columns:1fr}}
@media(max-width:420px){.bp-step .io{grid-template-columns:1fr}}

/* ── Handbook ──────────────────────────────────────────────────────────────── */
@keyframes bp-hb-grad{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
@keyframes bp-hb-shine{0%{transform:translateX(-140%) skewX(-18deg)}55%,100%{transform:translateX(480%) skewX(-18deg)}}
@keyframes bp-hb-book{0%,100%{transform:translateY(0) rotate(-4deg)}50%{transform:translateY(-14px) rotate(-1deg)}}
@keyframes bp-hb-bshadow{0%,100%{transform:scaleX(1);opacity:.5}50%{transform:scaleX(.8);opacity:.3}}
@keyframes bp-hb-ring{0%{box-shadow:0 0 0 0 rgba(37,99,235,.5)}70%,100%{box-shadow:0 0 0 16px rgba(37,99,235,0)}}
@keyframes bp-hb-orb{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(24px,-18px) scale(1.14)}}
@keyframes bp-hb-live{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(1.5)}}
.bp-handbook{
  position:relative;overflow:hidden;
  display:grid;grid-template-columns:clamp(190px,19vw,270px) minmax(0,1.5fr) minmax(300px,.9fr);
  gap:clamp(28px,4.5vw,56px);align-items:center;
  padding:calc(clamp(28px,3.6vw,48px) + 10px) clamp(28px,5vw,68px);
  border-radius:28px;border:1px solid rgba(37,99,235,.3);
  background:linear-gradient(120deg,var(--surface),var(--accent-soft),#DBEAFE,var(--surface));
  background-size:300% 300%;animation:bp-hb-grad 14s ease infinite;
  box-shadow:0 24px 60px -24px rgba(37,99,235,.45);
}
.bp-handbook:hover{box-shadow:0 32px 70px -22px rgba(37,99,235,.6);border-color:rgba(37,99,235,.5)}
.bp-hb-orb{position:absolute;border-radius:50%;pointer-events:none;filter:blur(40px);background:radial-gradient(circle,rgba(59,130,246,.38),transparent 70%);animation:bp-hb-orb 9s ease-in-out infinite}
.bp-hb-orb.o1{width:300px;height:300px;top:-110px;right:10%}
.bp-hb-orb.o2{width:240px;height:240px;bottom:-90px;left:16%;animation-delay:2.5s}
.bp-hb-shine{position:absolute;top:0;bottom:0;left:0;width:22%;pointer-events:none;background:linear-gradient(90deg,transparent,rgba(255,255,255,.55),transparent);animation:bp-hb-shine 7s ease-in-out infinite}
.bp-book-wrap,.bp-hb-copy,.bp-hb-form,.bp-handbook .bp-done-msg{position:relative;z-index:1}
.bp-book-wrap{display:flex;flex-direction:column;align-self:stretch;padding:0;min-width:0;min-height:0}
.bp-book{
  width:100%;flex:1;min-height:0;border-radius:6px 16px 16px 6px; /* same height as the text column */
  background:linear-gradient(135deg,var(--accent),#3B82F6 60%,#60A5FA);
  position:relative;box-shadow:var(--shadow-lift);
  animation:bp-hb-book 5s ease-in-out infinite;transform-origin:bottom center;
}
.bp-book::before{content:"SME";position:absolute;left:14px;top:14px;font-family:var(--f-mono);font-size:.66rem;color:#fff;letter-spacing:.12em}
.bp-book::after{content:"";position:absolute;left:8px;top:0;bottom:0;width:2px;background:rgba(255,255,255,.35)}
.bp-book span{position:absolute;left:14px;bottom:18px;color:#fff;font-weight:800;font-size:clamp(.85rem,1.3vw,1.1rem);line-height:1.2;letter-spacing:-.01em}
.bp-book-shadow{position:absolute;bottom:-12px;width:62%;height:14px;border-radius:50%;background:rgba(14,23,41,.35);filter:blur(8px);animation:bp-hb-bshadow 5s ease-in-out infinite}
.bp-hb-copy{display:grid;gap:10px;min-width:0}
.bp-hb-copy h3{font-size:clamp(1.4rem,2.3vw,2.05rem);line-height:1.15}
.bp-hb-copy .bp-small{font-size:1.02rem;max-width:52ch}
.bp-hb-live{display:inline-flex;align-items:center;gap:8px}
.bp-hb-live i{width:8px;height:8px;border-radius:50%;background:var(--accent);animation:bp-hb-live 1.8s ease-in-out infinite}
.bp-hb-chips{display:flex;flex-wrap:wrap;gap:8px;margin-top:4px}
.bp-hb-chips span{display:inline-flex;align-items:center;gap:6px;padding:8px 14px;border-radius:999px;font-size:.88rem;font-weight:600;background:rgba(255,255,255,.7);border:1px solid rgba(37,99,235,.2);color:var(--accent-strong);transition:transform .2s,background .2s}
.bp-hb-chips span:hover{transform:translateY(-2px);background:#fff}
.bp-hb-form{display:flex;gap:10px;flex-wrap:wrap}
.bp-hb-form .bp-input{padding:16px 20px;font-size:1rem}
.bp-hb-form .bp-btn{padding:15px 28px;font-size:1rem}
.bp-hb-form .bp-btn-primary:not(:disabled){animation:bp-hb-ring 2.2s ease-out infinite}
.bp-btn:disabled{opacity:.5;cursor:not-allowed;transform:none;box-shadow:none}
.bp-hb-err{flex-basis:100%;color:#B42318;font-size:.84rem}
.bp-input{
  padding:11px 16px;border-radius:999px;border:1.5px solid var(--line);
  background:var(--bg);color:var(--ink);font-family:var(--f-sans);font-size:.94rem;
  min-width:0;flex:1 1 200px;
  transition:border-color .2s,box-shadow .2s;
}
.bp-input:focus{outline:none;border-color:var(--accent);box-shadow:0 0 0 3px rgba(37,99,235,.15)}
@media(max-width:1000px){.bp-handbook{grid-template-columns:clamp(120px,22vw,170px) 1fr}.bp-hb-form,.bp-handbook .bp-done-msg{grid-column:1/-1}}
@media(max-width:640px){.bp-handbook{grid-template-columns:1fr}.bp-book-wrap{display:none}}

/* ── FAQ ───────────────────────────────────────────────────────────────────── */
.bp-faq-wrap{display:grid;grid-template-columns:1.1fr .9fr;gap:40px;align-items:start}
.bp-faq details{border-top:1px solid var(--line);padding:16px 0;transition:background .15s}
.bp-faq details:last-child{border-bottom:1px solid var(--line)}
.bp-faq details[open]{background:var(--tint);border-radius:var(--r-sm);padding:16px}
.bp-faq summary{cursor:pointer;font-weight:600;list-style:none;display:flex;justify-content:space-between;gap:16px;transition:color .15s}
.bp-faq summary::-webkit-details-marker{display:none}
.bp-faq summary::after{content:"+";font-family:var(--f-mono);color:var(--accent);font-weight:500;font-size:1.1rem;line-height:1;transition:transform .3s}
.bp-faq details[open] summary::after{content:"–"}
.bp-faq summary:hover{color:var(--accent)}
.bp-faq details p{color:var(--muted);margin-top:10px}
.bp-contact{padding:24px;display:grid;gap:12px}
.bp-contact .row{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.bp-contact label{display:grid;gap:5px;font-size:.84rem;font-weight:600}
.bp-contact input,.bp-contact select,.bp-contact textarea{
  padding:10px 14px;border-radius:var(--r-sm);border:1.5px solid var(--line);
  background:var(--bg);color:var(--ink);font-family:var(--f-sans);font-size:.92rem;width:100%;
  transition:border-color .2s,box-shadow .2s;
}
.bp-contact input:focus,.bp-contact select:focus,.bp-contact textarea:focus{
  outline:none;border-color:var(--accent);box-shadow:0 0 0 3px rgba(37,99,235,.12);
}
.bp-contact textarea{min-height:90px;resize:vertical}
.bp-done-msg{padding:12px 14px;border-radius:var(--r-sm);background:var(--good-soft);color:var(--good);font-size:.9rem;font-weight:500}
@media(max-width:900px){.bp-faq-wrap{grid-template-columns:1fr}}
@media(max-width:480px){.bp-contact .row{grid-template-columns:1fr}}

/* ── Category cards (Section 3) ────────────────────────────────────────────── */
.bp-cat-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:16px;align-items:start}
.bp-cat-card{
  background:var(--surface);border:1px solid var(--line);border-radius:var(--r);
  padding:22px;display:grid;gap:14px;
  transition:transform .22s cubic-bezier(.16,1,.3,1),box-shadow .22s,border-color .22s;
}
.bp-cat-card:hover{transform:translateY(-5px);box-shadow:0 16px 40px -12px rgba(37,99,235,.18);border-color:rgba(37,99,235,.25)}
.bp-cat-num{font-family:var(--f-mono);font-size:.68rem;color:var(--accent);font-weight:600;text-transform:uppercase;letter-spacing:.09em}
.bp-cat-fields{display:flex;flex-wrap:wrap;gap:8px}
.bp-cat-fld{
  display:inline-flex;align-items:center;gap:7px;
  font-size:.82rem;color:var(--ink);padding:5px 10px;
  border-radius:6px;background:var(--tint);border:1px solid var(--line);
  transition:transform .15s;
}
.bp-cat-fld:hover{transform:translateY(-1px)}
.bp-cat-fld.frep{background:var(--amber-soft);border-color:transparent;color:var(--amber)}
.bp-cat-fld.fauth{background:var(--violet-soft);border-color:transparent;color:var(--violet)}
.bp-cat-fld.flive{border-style:dashed;border-color:var(--good);color:var(--good)}
@media(max-width:800px){.bp-cat-grid{grid-template-columns:1fr}}

/* ── Why Partner (Section 8) ────────────────────────────────────────────────── */
.bp-why{background:var(--deep);color:var(--deep-ink);padding-block:clamp(56px,8vw,96px)}
.bp-why .bp-eyebrow{color:#8FB2FF}
.bp-why h2{color:var(--deep-ink)}
.bp-why .bp-lede{color:var(--deep-muted)}
.bp-why-cards{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-top:40px}
.bp-why-card{
  background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.1);
  border-radius:var(--r);padding:24px;display:grid;gap:10px;align-content:start;
  transition:transform .22s cubic-bezier(.16,1,.3,1),background .22s,border-color .22s;
}
.bp-why-card:hover{transform:translateY(-6px);background:rgba(255,255,255,.09);border-color:rgba(255,255,255,.2)}
.bp-why-card h3{color:var(--deep-ink)}
.bp-why-card p{color:var(--deep-muted);font-size:.92rem;margin:0}
.bp-why-icon{
  width:46px;height:46px;background:rgba(37,99,235,.3);border-radius:11px;
  display:grid;place-items:center;color:#8FB2FF;
  transition:background .2s,transform .2s;
}
.bp-why-card:hover .bp-why-icon{background:rgba(37,99,235,.5);transform:scale(1.08)}
.bp-plates{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-top:28px}
.bp-plate{
  display:flex;gap:12px;align-items:flex-start;padding:16px;
  background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:var(--r-sm);
  transition:background .2s,border-color .2s,transform .2s;
}
.bp-plate:hover{background:rgba(255,255,255,.08);border-color:rgba(255,255,255,.15);transform:translateY(-2px)}
.bp-plate-dot{width:8px;height:8px;border-radius:50%;background:#5B8DF6;flex:none;margin-top:6px}
.bp-plate b{font-size:.93rem;color:var(--deep-ink);display:block;margin-bottom:2px;font-weight:700}
.bp-plate span{font-size:.84rem;color:var(--deep-muted)}
.bp-why-btn{
  display:inline-flex;align-items:center;gap:8px;padding:13px 24px;border-radius:999px;
  font-weight:700;font-size:.95rem;border:none;cursor:pointer;
  font-family:var(--f-sans);
  transition:background .2s,transform .18s,box-shadow .2s;
  will-change:transform;
}
.bp-why-btn:hover{transform:translateY(-2px)}
.bp-why-btn:active{transform:translateY(0)}
.bp-why-btn-white{background:#fff;color:#0B1430;box-shadow:0 4px 14px rgba(0,0,0,.25)}
.bp-why-btn-white:hover{background:#DCE6FF;box-shadow:0 8px 22px rgba(0,0,0,.35)}
@media(max-width:860px){.bp-why-cards{grid-template-columns:1fr 1fr}.bp-plates{grid-template-columns:1fr 1fr}}
@media(max-width:560px){.bp-why-cards{grid-template-columns:1fr}.bp-plates{grid-template-columns:1fr}}

/* ── Categories: view more ─────────────────────────────────────────────────── */
.bp-more-btn{padding:12px 26px}

/* ── Gap score ─────────────────────────────────────────────────────────────── */
.bp-q-page{animation:bp-fade-up .4s cubic-bezier(.16,1,.3,1) both}
.bp-quiz-nav{display:flex;justify-content:space-between;gap:10px}
.bp-btn-sm{padding:9px 18px;font-size:.88rem}
.bp-meter{height:8px;background:var(--tint);border-radius:999px;overflow:hidden}
.bp-meter i{display:block;height:100%;border-radius:inherit;background:linear-gradient(90deg,var(--accent),#60A5FA);transition:width .45s ease}
.bp-gap-list{display:grid;gap:8px;max-height:250px;overflow:auto;padding-right:2px}
.bp-gap-item{display:flex;gap:12px;justify-content:space-between;align-items:flex-start;font-size:.88rem;padding:9px 12px;border-radius:var(--r-sm);background:var(--tint)}
.bp-gap-tag{flex:none;font-family:var(--f-mono);font-size:.68rem;font-weight:600;padding:3px 9px;border-radius:999px;white-space:nowrap}

/* ── Why partner: message + interactive plate ──────────────────────────────── */
.bp-why-split{display:grid;grid-template-columns:1fr 1fr;gap:clamp(32px,6vw,80px);align-items:center}
.bp-why-split .bp-head{margin-bottom:0}
.bp-wp-sub{font-weight:700;color:var(--deep-ink);font-size:1.05rem;margin-bottom:6px}

.bp-wp-panel{border-left:2px solid rgba(255,255,255,.14);padding-left:clamp(20px,3vw,36px);display:grid;gap:14px}
.bp-wp-top{display:flex;justify-content:space-between;align-items:baseline;gap:12px;flex-wrap:wrap}
.bp-wp-top b{color:var(--deep-ink);font-size:1.2rem;font-weight:700}
.bp-wp-count{font-family:var(--f-mono);font-size:.8rem;color:#8FB2FF;font-variant-numeric:tabular-nums}
.bp-wp-bar{height:6px;border-radius:999px;background:rgba(255,255,255,.1);overflow:hidden}
.bp-wp-bar i{display:block;height:100%;border-radius:inherit;background:linear-gradient(90deg,#3B82F6,#93C5FD);transition:width .5s cubic-bezier(.16,1,.3,1)}

.bp-wp-tasks{list-style:none;margin:0;padding:0;display:grid;grid-template-columns:1fr 1fr;gap:0 22px}
.bp-wp-task{
  all:unset;box-sizing:border-box;width:100%;cursor:pointer;display:flex;align-items:center;gap:12px;
  padding:13px 0;border-bottom:1px solid rgba(255,255,255,.1);
  color:var(--deep-ink);font-size:.97rem;font-weight:500;
  transition:transform .35s cubic-bezier(.16,1,.3,1),color .3s,border-color .3s;
}
.bp-wp-task:hover{transform:translateX(4px);border-color:rgba(147,197,253,.5)}
.bp-wp-task:focus-visible{outline:2px solid #8FB2FF;outline-offset:2px;border-radius:6px}
.bp-wp-check{
  flex:none;width:24px;height:24px;border-radius:50%;display:grid;place-items:center;
  border:2px solid rgba(255,255,255,.3);color:transparent;
  transition:background .3s,border-color .3s,color .3s,transform .4s cubic-bezier(.34,1.56,.64,1);
}
.bp-wp-label{
  background:linear-gradient(currentColor,currentColor) no-repeat left center/0% 2px;
  transition:background-size .45s ease,opacity .3s;
}
.bp-wp-task.done{color:var(--deep-muted)}
.bp-wp-task.done .bp-wp-check{background:#2563EB;border-color:#60A5FA;color:#fff;transform:scale(1.12)}
.bp-wp-task.done .bp-wp-label{background-size:100% 2px;opacity:.75}

.bp-wp-actions{display:flex;align-items:center;gap:14px;flex-wrap:wrap;padding-top:4px}
.bp-wp-btn{
  all:unset;box-sizing:border-box;cursor:pointer;padding:9px 18px;border-radius:999px;
  border:1.5px solid rgba(147,197,253,.5);color:#BFD4FF;font-weight:600;font-size:.88rem;
  transition:background .2s,color .2s,transform .18s,border-color .2s;
}
.bp-wp-btn:hover{background:rgba(147,197,253,.15);color:#fff;border-color:#93C5FD;transform:translateY(-2px)}
.bp-wp-btn:focus-visible{outline:2px solid #8FB2FF;outline-offset:2px}

/* getting started */
.bp-wp-row2{margin-top:clamp(32px,4.5vw,52px);display:grid;grid-template-columns:1fr auto;gap:clamp(24px,4vw,48px);align-items:end}
.bp-wp-steps{display:grid;grid-template-columns:repeat(3,1fr);margin-top:14px}
.bp-wp-step{position:relative;display:grid;gap:4px;align-content:start;padding:30px 22px 0 0;transition:transform .3s cubic-bezier(.16,1,.3,1)}
.bp-wp-step::before{content:"";position:absolute;top:7px;left:0;right:0;height:2px;background:rgba(255,255,255,.18)}
.bp-wp-step::after{
  content:"";position:absolute;top:0;left:0;width:16px;height:16px;border-radius:50%;
  background:var(--deep);border:2px solid #5B8DF6;
  transition:background .3s,box-shadow .3s,transform .3s;
}
.bp-wp-step:hover{transform:translateY(-4px)}
.bp-wp-step:hover::after{background:#3B82F6;box-shadow:0 0 0 5px rgba(59,130,246,.3);transform:scale(1.15)}
.bp-wp-step:hover::before{background:linear-gradient(90deg,#3B82F6,rgba(255,255,255,.18))}
.bp-wp-no{font-family:var(--f-mono);font-size:.72rem;font-weight:600;color:#5B8DF6;letter-spacing:.08em}
.bp-wp-step b{color:var(--deep-ink);font-size:1.02rem;font-weight:700}
.bp-wp-step span:last-child{color:var(--deep-muted);font-size:.9rem;line-height:1.5}

@media(max-width:860px){
  .bp-why-split{grid-template-columns:1fr}
  .bp-wp-row2{grid-template-columns:1fr;align-items:start}
  .bp-wp-panel{border-left:0;padding-left:0}
}
@media(max-width:620px){
  .bp-wp-tasks{grid-template-columns:1fr}
  .bp-wp-steps{grid-template-columns:1fr;gap:18px}
}


/* ── Responsive padding ────────────────────────────────────────────────────── */
@media(max-width:640px){
  .bp-page section{padding-block:clamp(60px,8vw,64px)}
  .bp-head{margin-bottom:28px}
  .bp-lede{font-size:.97rem}
}
@media(max-width:480px){
  .bp-btns{flex-direction:column;align-items:stretch}
  .bp-btn{justify-content:center}
  .bp-trust{gap:10px}
  .bp-handbook{padding:18px}
}
`;

/* ── Styles injection ────────────────────────────────────────────────────────── */
function usePageStyles() {
  useEffect(() => {
    let el = document.getElementById("bp-styles");
    if (!el) { el = document.createElement("style"); el.id = "bp-styles"; document.head.appendChild(el); }
    el.textContent = PAGE_CSS;
    return () => { document.getElementById("bp-styles")?.remove(); };
  }, []);
}

/* ── Scroll-reveal hook ──────────────────────────────────────────────────────── */
function useReveal() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) { e.target.classList.add("on"); obs.unobserve(e.target); }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -50px 0px" }
    );
    document.querySelectorAll(".bp-rv,.bp-rv-l,.bp-rv-r,.bp-rv-s").forEach(el => obs.observe(el));
    return () => obs.disconnect();
  });
}

/* ── Icons ───────────────────────────────────────────────────────────────────── */
const IArrow = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 8h10M9 4l4 4-4 4"/>
  </svg>
);
const ICheck = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 8.5l3 3 7-7"/>
  </svg>
);
const ICar = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
    <path d="M3 14l2-5a2 2 0 012-1.4h10A2 2 0 0119 9l2 5v4h-2a2 2 0 01-4 0H9a2 2 0 01-4 0H3z"/>
  </svg>
);
const IBike = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="6" cy="16" r="3.5"/><circle cx="18" cy="16" r="3.5"/>
    <path d="M6 16l4-7h6l2 7M10 9l3 7M14 6h3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IFactory = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
    <path d="M3 20V10l5 3V10l5 3V6h3l1-3h2l1 3v14z"/>
  </svg>
);
const IDb = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <ellipse cx="12" cy="6" rx="7" ry="2.8"/>
    <path d="M5 6v12c0 1.5 3 2.8 7 2.8s7-1.3 7-2.8V6M5 12c0 1.5 3 2.8 7 2.8s7-1.3 7-2.8"/>
  </svg>
);
const IUsers = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <circle cx="9" cy="9" r="3.2"/>
    <path d="M3.5 19c.8-3 3-4.5 5.5-4.5s4.7 1.5 5.5 4.5M16 6.5a3 3 0 010 5.6M17.5 14.6c1.6.6 2.6 2 3 4.4"/>
  </svg>
);
const IScan = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <path d="M4 8V5a1 1 0 011-1h3M16 4h3a1 1 0 011 1v3M20 16v3a1 1 0 01-1 1h-3M8 20H5a1 1 0 01-1-1v-3M4 12h16"/>
  </svg>
);
const ISeal = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="12" cy="10" r="6"/>
    <path d="M9 15l-2 6 5-2 5 2-2-6" strokeLinejoin="round"/>
    <path d="M9.5 10l1.7 1.7 3.3-3.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ICloud = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
    <path d="M7 18a4.5 4.5 0 01-.5-9 6 6 0 0111.4 1.6A3.8 3.8 0 0117.5 18z"/>
  </svg>
);
const IShield = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" strokeLinejoin="round"/>
    <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IChain = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <rect x="3" y="8" width="8" height="8" rx="2"/>
    <rect x="13" y="8" width="8" height="8" rx="2"/>
    <path d="M11 12h2"/>
  </svg>
);
const IBattery = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <rect x="3" y="7" width="16" height="10" rx="2"/>
    <path d="M21 10.5v3" strokeLinecap="round" strokeWidth="1.8"/>
    <path d="M11.5 9l-2.5 3.5h3L9.5 15" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IX = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M4 4l8 8M12 4l-8 8"/>
  </svg>
);
const IPlug = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3v5M15 3v5M6 8h12v3a6 6 0 01-12 0zM12 17v4"/>
  </svg>
);
const IGlobe = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="12" cy="12" r="8.5"/>
    <path d="M3.5 12h17M12 3.5c3 3 3 14 0 17M12 3.5c-3 3-3 14 0 17"/>
  </svg>
);

/* ── QR placeholder (seeded PRNG, visual only) ──────────────────────────────── */
function QrCode() {
  const svgRef = useRef(null);
  useEffect(() => {
    if (!svgRef.current) return;
    const n = 25; let seed = 7;
    const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
    const cells = [];
    const finder = (x, y) => {
      cells.push(`<path d="M${x} ${y}h7v7h-7z M${x+1} ${y+1}v5h5v-5z" fill-rule="evenodd"/>`);
      cells.push(`<rect x="${x+2}" y="${y+2}" width="3" height="3"/>`);
    };
    finder(0,0); finder(18,0); finder(0,18);
    for (let y = 0; y < n; y++)
      for (let x = 0; x < n; x++) {
        const inF = (x<8&&y<8)||(x>16&&y<8)||(x<8&&y>16);
        if (!inF && rnd() > 0.52) cells.push(`<rect x="${x}" y="${y}" width="1" height="1"/>`);
      }
    svgRef.current.innerHTML = `<g fill="currentColor">${cells.join("")}</g>`;
  }, []);
  return <svg ref={svgRef} className="pp-qr" viewBox="0 0 25 25" aria-label="QR code placeholder"/>;
}

/* ── Battery SVG art ────────────────────────────────────────────────────────── */
function BatterySVG() {
  return (
    <svg viewBox="0 0 220 330" width="100%" role="img" aria-label="Battery pack">
      <defs>
        <linearGradient id="bbg1" x1="0" x2="1">
          <stop offset="0" stopColor="#1D4ED8"/><stop offset=".55" stopColor="#3B82F6"/><stop offset="1" stopColor="#1E3A8A"/>
        </linearGradient>
        <linearGradient id="bbg2" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#93C5FD"/><stop offset="1" stopColor="#2563EB"/>
        </linearGradient>
      </defs>
      <rect x="78" y="8" width="64" height="22" rx="6" fill="#1E3A8A"/>
      <rect x="20" y="26" width="180" height="296" rx="26" fill="url(#bbg1)"/>
      <rect x="34" y="40" width="152" height="268" rx="18" fill="none" stroke="rgba(255,255,255,.25)"/>
      <g fill="url(#bbg2)" opacity=".9">
        {[0,1,2,3,4].map(i => <rect key={i} x="48" y={60+i*48} width="124" height="34" rx="8"/>)}
      </g>
      <path d="M118 120l-26 46h22l-10 40 30-52h-22z" fill="#FFFFFF"/>
    </svg>
  );
}

/* ── Passport card ──────────────────────────────────────────────────────────── */
function PassportCard({ full = false }) {
  return (
    <div className="passport" style={{ width: "100%" }}>
      <div className="pp-top">
        <div>
          <div className="t">Battery Passport</div>
          <div className="pp-id">PVX-BAT-7F3A-0219</div>
        </div>
        <QrCode />
      </div>
      <div className="pp-rows">
        <div className="pp-row"><span>Chemistry</span><b>NMC 811</b></div>
        <div className="pp-row"><span>Rated capacity</span><b>75 kWh</b></div>
        <div className="pp-row">
          <span>Carbon footprint</span>
          <b>61 kg CO₂e/kWh <span className="cls">B</span></b>
        </div>
        {full && (
          <>
            <div className="pp-row"><span>Recycled content</span><b>Co 16% · Li 6% · Ni 6%</b></div>
            <div className="pp-row"><span>Expected lifetime</span><b>3,000 cycles</b></div>
          </>
        )}
        <div className="pp-row" style={{ display: "grid", gap: "6px" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>State of health</span><b>98%</b>
          </div>
          <div className="bar"><i style={{ width: "98%" }} /></div>
        </div>
      </div>
    </div>
  );
}

/* ── Data ────────────────────────────────────────────────────────────────────── */
const P = "pub", R = "rep", A = "auth", L = "live";

const SECTIONS = [
  ["Identity", "Who made it, what it is and where it stands today.", [
    ["Manufacturer & ID", P], ["Unique battery ID", P], ["Model", P],
    ["Battery category", P], ["Place & date of manufacture", P],
    ["Weight", P], ["Status: original, repurposed, re-used or waste", P],
  ]],
  ["Materials", "What the battery is made of.", [
    ["Chemistry", P], ["Critical raw materials", P],
    ["Hazardous substances", P], ["Cathode, anode & electrolyte detail", R],
  ]],
  ["Carbon footprint", "Lifecycle emissions, declared per model.", [
    ["Total CO₂ per kWh", P], ["Share by lifecycle stage", P],
    ["Performance class", P], ["Link to supporting study", P],
  ]],
  ["Due diligence", "Responsible sourcing of key raw materials.", [
    ["Cobalt, lithium, nickel & graphite report", P], ["Third-party verification", P],
  ]],
  ["Performance", "How it performs on day one, and as it ages.", [
    ["Rated capacity", P], ["Min / nominal / max voltage", P], ["Power capability", P],
    ["Expected lifetime (cycles)", P], ["Temperature range", P],
    ["Round-trip efficiency", P], ["Internal resistance", P],
    ["State of health", L], ["Charge cycles", L], ["Negative events", L],
  ]],
  ["Circularity", "What happens when its first life ends.", [
    ["Recycled content (Co, Li, Ni, Pb)", P], ["Renewable content", P],
    ["Collection & take-back info", P], ["Dismantling instructions", R],
    ["Spare-part sources", R], ["Safety measures", R],
  ]],
  ["Conformity", "Proof it meets EU rules.", [
    ["EU declaration of conformity", P], ["CE marking", P],
    ["Labels & symbols", P], ["Test reports", A],
  ]],
];

const FIELD_COLOR = { pub: "var(--accent)", rep: "var(--amber)", auth: "var(--violet)", live: "var(--good)" };
const FIELD_CLS   = { pub: "", rep: "rep", auth: "auth", live: "live" };

const QUIZ_QS = [
  "Do you know which economic-operator role you hold under the regulation?",
  "Does your company hold an eIDAS Qualified Electronic Seal?",
  "Are a unique battery identifier and QR data carrier already in place?",
  "Do you hold documented material-composition and critical-raw-material data?",
  "Do you have a lifecycle carbon-footprint declaration for your models?",
  "Are your due-diligence records complete for cobalt, lithium, nickel and graphite?",
  "Can you obtain the data your upstream suppliers hold?",
  "Do you have performance and durability figures: capacity, cycle life, state of health?",
  "Do you have recycled-content and end-of-life information?",
  "Are you ready to register your batteries in the EU DPP registry?",
];
const QUIZ_VALS = { Yes: 1, Partly: 0.5, "Not yet": 0 };

const PROVINYX_LOGO = "data:image/png;base64,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";

/* ── Section 5 - How it works ───────────────────────────────────────────────── */

const HIW_STEPS = [
  {
    n: 1,
    title: "Connect data",
    desc: "Start from what you already have. Import spreadsheets or your ERP, and suppliers fill their own sections through a secure link.",
    chips: ["ERP / CSV import", "Supplier portal", "Document upload"],
  },
  {
    n: 2,
    title: "Create passport",
    desc: "We map your data to Annex XIII and issue a unique battery ID with a printable QR label.",
    chips: ["Annex XIII mapping", "Unique ID", "QR label"],
  },
  {
    n: 3,
    title: "Support registration",
    desc: "We set up your eIDAS Qualified Electronic Seal and register every passport in the EU registry.",
    chips: ["QSeal set-up", "EU registry"],
  },
  {
    n: 4,
    title: "Maintain",
    desc: "Passports stay online for the battery's whole life and update as rules, ownership and battery health change.",
    chips: ["Hosted for life", "Live data", "Rule updates"],
  },
];

function HowItWorksSection() {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const fitRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const lastRef = useRef(null);
  const [soh, setSoh] = useState(94);
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [scale, setScale] = useState(1);

  // ResizeObserver for scaling
  useEffect(() => {
    if (!fitRef.current) return;
    const ro = new ResizeObserver(() => {
      const w = fitRef.current?.clientWidth ?? 790;
      const s = Math.min(1.45, w / 790); // allow the diagram to grow on wide screens
      setScale(s);
    });
    ro.observe(fitRef.current);
    return () => ro.disconnect();
  }, []);

  // IntersectionObserver for auto-play
  useEffect(() => {
    if (!sectionRef.current) return;
    const ob = new IntersectionObserver(
      ([e]) => setInView(e.isIntersecting),
      { threshold: 0.35 }
    );
    ob.observe(sectionRef.current);
    return () => ob.disconnect();
  }, []);

  // SoH ticker
  useEffect(() => {
    const t = setInterval(() => {
      if (step === 3) setSoh(v => (v >= 94 ? 93 : 94));
    }, 1600);
    return () => clearInterval(t);
  }, [step]);

  // rAF loop
  const tick = useCallback((now) => {
    if (!playing || !inView) { lastRef.current = null; rafRef.current = requestAnimationFrame(tick); return; }
    if (lastRef.current == null) { lastRef.current = now; }
    const delta = now - lastRef.current;
    lastRef.current = now;
    setProgress(p => {
      const next = p + delta / 4800;
      if (next >= 1) {
        setStep(s => (s + 1) % 4);
        return 0;
      }
      return next;
    });
    rafRef.current = requestAnimationFrame(tick);
  }, [playing, inView]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [tick]);

  const goStep = (n) => { setStep(n); setProgress(0); lastRef.current = null; };
  const togglePlay = () => { setPlaying(p => !p); lastRef.current = null; };

  const caption = `Step ${step + 1} of 4 · ${HIW_STEPS[step].title}`;

  return (
    <section id="how-it-works" ref={sectionRef} style={{ background: "var(--surface)" }}>
      <div className="max-w-7xl mx-auto px-6">
        <FadeIn className="mb-12">
          <p className="text-sm font-semibold text-blue-600 uppercase tracking-widest mb-2">How it works</p>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-3">From your data to a registered passport</h2>
          <p className="text-base text-slate-500">Choose a step to see what happens, or let it play.</p>
        </FadeIn>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* step list */}
          <div className="flex flex-col gap-1.5 w-full lg:w-[330px] shrink-0">
            {HIW_STEPS.map((s, i) => {
              const on = step === i;
              return (
                <button
                  key={i}
                  onClick={() => goStep(i)}
                  aria-current={on ? "step" : undefined}
                  className={`text-left rounded-2xl px-4 py-3 border transition-all duration-500 ease-out ${
                    on
                      ? "bg-white border-slate-200/70 shadow-[0_14px_36px_-10px_rgba(30,64,175,.28)]"
                      : "bg-transparent border-transparent hover:bg-white/60"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-9 h-9 rounded-full text-sm font-bold flex items-center justify-center shrink-0 transition-all duration-500 ${
                        on ? "bg-blue-600 text-white scale-105" : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      {s.n}
                    </span>
                    <span className="text-base font-semibold text-slate-900">{s.title}</span>
                  </div>
                  {/* smooth expand: grid rows 0fr -> 1fr */}
                  <div
                    className="grid transition-[grid-template-rows,opacity] duration-500 ease-out"
                    style={{ gridTemplateRows: on ? "1fr" : "0fr", opacity: on ? 1 : 0 }}
                  >
                    <div className="overflow-hidden">
                      <div className="pl-12 pt-2">
                        <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                        <div className="flex flex-wrap gap-1.5 mt-2.5">
                          {s.chips.map(c => (
                            <span key={c} className="text-[11px] font-mono px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">{c}</span>
                          ))}
                        </div>
                        <div className="mt-3 h-1 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-600 rounded-full transition-none"
                            style={{ width: `${on ? progress * 100 : 0}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}

            <button
              onClick={togglePlay}
              className="mt-2 ml-1 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-slate-900 border-[1.5px] border-blue-600 bg-white/60 hover:bg-blue-50 transition-colors self-start"
            >
              {playing ? (
                <>
                  <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M5 3v10M11 3v10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                  </svg>
                  Pause
                </>
              ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M5 3l8 5-8 5z" fill="currentColor" />
                  </svg>
                  Play
                </>
              )}
            </button>
          </div>

          {/* diagram */}
          <div className="flex-1 min-w-0">
            <div
              ref={fitRef}
              className="relative w-full overflow-hidden rounded-3xl bg-white border border-slate-200/70 shadow-[0_18px_50px_-18px_rgba(30,64,175,.22)]"
              style={{
                height: 410 * scale,
                backgroundImage: "radial-gradient(#cbd5e1 1px, transparent 1px)",
                backgroundSize: "22px 22px",
              }}
            >
              <div
                ref={canvasRef}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: 790,
                  height: 410,
                  transformOrigin: "top left",
                  transform: `scale(${scale})`,
                }}
              >
                <HiwCanvas step={step} soh={soh} onGo={goStep} />
              </div>
            </div>
            <p className="text-sm text-slate-500 text-center mt-3" aria-live="polite">{caption}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function HiwCanvas({ step, soh, onGo }) {
  const at = (n) => step >= n;

  return (
    <div style={{ position: "relative", width: 790, height: 410 }}>
      {/* SVG wires */}
      <svg
        width="790" height="410" viewBox="0 0 790 410"
        aria-hidden="true"
        style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
      >
        <defs>
          <marker id="ah" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
            <path d="M0,1 L5,3 L0,5 Z" fill="#2563eb" />
          </marker>
        </defs>

        {/* wire 0 - top input → core */}
        <path
          id="hw0"
          d="M234 87 C 268 87, 262 210, 294 210"
          fill="none"
          stroke={at(0) ? "#2563eb" : "#cbd5e1"}
          strokeWidth="1.8"
          strokeOpacity={at(0) ? 1 : 0.4}
        />
        {/* wire 1 - middle input → core */}
        <path
          id="hw1"
          d="M234 210 L294 210"
          fill="none"
          stroke={at(0) ? "#2563eb" : "#cbd5e1"}
          strokeWidth="1.8"
          strokeOpacity={at(0) ? 1 : 0.4}
        />
        {/* wire 2 - bottom input → core */}
        <path
          id="hw2"
          d="M234 333 C 268 333, 262 210, 294 210"
          fill="none"
          stroke={at(0) ? "#2563eb" : "#cbd5e1"}
          strokeWidth="1.8"
          strokeOpacity={at(0) ? 1 : 0.4}
        />
        {/* chev 0 */}
        <path d="M-10 -7L0 0L-10 7" transform="translate(296 210)" fill="none" stroke={at(0) ? "#2563eb" : "#cbd5e1"} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" strokeOpacity={at(0) ? 1 : 0.4} />

        {/* wire 3 - core → passport */}
        <path
          id="hw3"
          d="M454 175 C 494 175, 494 140, 534 140"
          fill="none"
          stroke={at(1) ? "#2563eb" : "#cbd5e1"}
          strokeWidth="1.8"
          strokeOpacity={at(1) ? 1 : 0.4}
        />
        <path d="M-10 -7L0 0L-10 7" transform="translate(536 140)" fill="none" stroke={at(1) ? "#2563eb" : "#cbd5e1"} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" strokeOpacity={at(1) ? 1 : 0.4} />

        {/* wire 4 - core → registry (dashed) */}
        <path
          id="hw4"
          d="M375 290 L375 362 L534 362"
          fill="none"
          stroke={at(2) ? "#2563eb" : "#cbd5e1"}
          strokeWidth="1.8"
          strokeDasharray="6,4"
          strokeOpacity={at(2) ? 1 : 0.4}
        />
        <path d="M-10 -7L0 0L-10 7" transform="translate(536 362)" fill="none" stroke={at(2) ? "#2563eb" : "#cbd5e1"} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" strokeOpacity={at(2) ? 1 : 0.4} />

        {/* animated dots - step 0 wires */}
        {at(0) && (
          <g fill="#2563eb">
            <circle r="5" opacity="0.85">
              <animateMotion dur="1.5s" repeatCount="indefinite"><mpath href="#hw0" /></animateMotion>
            </circle>
            <circle r="5" opacity="0.85">
              <animateMotion dur="1.5s" begin="0.5s" repeatCount="indefinite"><mpath href="#hw1" /></animateMotion>
            </circle>
            <circle r="5" opacity="0.85">
              <animateMotion dur="1.5s" begin="1s" repeatCount="indefinite"><mpath href="#hw2" /></animateMotion>
            </circle>
          </g>
        )}
        {at(1) && (
          <g fill="#2563eb">
            <circle r="5" opacity="0.85">
              <animateMotion dur="1.2s" repeatCount="indefinite"><mpath href="#hw3" /></animateMotion>
            </circle>
          </g>
        )}
        {at(2) && (
          <g fill="#2563eb">
            <circle r="5" opacity="0.85">
              <animateMotion dur="1.8s" repeatCount="indefinite"><mpath href="#hw4" /></animateMotion>
            </circle>
          </g>
        )}
      </svg>

      {/* Left input nodes */}
      <HiwNode top={54} active={at(0)} onClick={() => onGo(0)}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
          <ellipse cx="12" cy="6" rx="7" ry="2.8" />
          <path d="M5 6v12c0 1.5 3 2.8 7 2.8s7-1.3 7-2.8V6M5 12c0 1.5 3 2.8 7 2.8s7-1.3 7-2.8" />
        </svg>
        <span className="text-xs font-semibold text-slate-800">Product data</span>
        <span className="text-xs text-slate-500">ERP or spreadsheets</span>
      </HiwNode>

      <HiwNode top={177} active={at(0)} onClick={() => onGo(0)}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
          <path d="M3 20V10l5 3V10l5 3V6h3l1-3h2l1 3v14z" />
        </svg>
        <span className="text-xs font-semibold text-slate-800">Supplier data</span>
        <span className="text-xs text-slate-500">Via a secure link</span>
      </HiwNode>

      <HiwNode top={300} active={at(0)} onClick={() => onGo(0)}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" />
          <path d="M14 3v5h5M9 13h6M9 17h6" />
        </svg>
        <span className="text-xs font-semibold text-slate-800">Documents</span>
        <span className="text-xs text-slate-500">Test reports, declarations</span>
      </HiwNode>

      {/* Core (Provinyx platform) */}
      <button
        onClick={() => onGo(1)}
        style={{
          position: "absolute",
          left: 300, top: 135,
          width: 150, height: 150,
          borderRadius: 28,
          background: at(0) ? "linear-gradient(135deg,#2563eb,#1d4ed8)" : "#e2e8f0",
          border: "none",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          boxShadow: at(0) ? "0 8px 32px rgba(37,99,235,0.3)" : "none",
          transition: "all 0.4s ease",
        }}
        aria-label="Provinyx platform"
      >
        <img src={PROVINYX_LOGO} alt="Provinyx" style={{ width: 112, height: "auto", opacity: at(0) ? 1 : 0.4 }} />
        {/* QSeal badge at step 2 */}
        {at(2) && (
          <span
            style={{
              position: "absolute", top: -10, right: -10,
              background: "#2563eb", color: "white",
              fontSize: 10, fontWeight: 700,
              padding: "2px 7px", borderRadius: 20,
              display: "flex", alignItems: "center", gap: 4,
              boxShadow: "0 2px 8px rgba(37,99,235,0.4)",
            }}
          >
            <svg width="10" height="10" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            QSeal
          </span>
        )}
      </button>

      {/* Battery passport card */}
      <button
        onClick={() => onGo(1)}
        style={{
          position: "absolute",
          left: 540, top: 40,
          width: 220, height: 210,
          borderRadius: 16,
          background: at(1) ? "white" : "#f1f5f9",
          border: `1.5px solid ${at(1) ? "#bfdbfe" : "#e2e8f0"}`,
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          gap: 0,
          overflow: "hidden",
          boxShadow: at(1) ? "0 4px 20px rgba(37,99,235,0.1)" : "none",
          transition: "all 0.4s ease",
        }}
      >
        <div
          style={{
            background: at(1) ? "linear-gradient(135deg,#2563eb,#1d4ed8)" : "#94a3b8",
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ color: "white", fontSize: 10, fontWeight: 700 }}>Battery passport</span>
          <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 10 }}>PVX-BAT-7F3A</span>
        </div>
        <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
          <div style={{ height: 8, borderRadius: 4, background: at(1) ? "#bfdbfe" : "#e2e8f0", width: "100%" }} />
          <div style={{ height: 8, borderRadius: 4, background: at(1) ? "#bfdbfe" : "#e2e8f0", width: "70%" }} />
          <div style={{ height: 8, borderRadius: 4, background: at(1) ? "#bfdbfe" : "#e2e8f0", width: "86%" }} />
          {/* Live SoH */}
          {at(3) && (
            <div
              style={{
                marginTop: 8,
                padding: "4px 8px",
                borderRadius: 20,
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 11,
                color: "#2563eb",
                fontWeight: 600,
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563eb", display: "inline-block", animation: "bp-pulse-dot 2s infinite" }} />
              SoH {soh}% · live
            </div>
          )}
          {/* QR placeholder */}
          <div
            style={{
              position: "absolute", bottom: 10, right: 10,
              width: 56, height: 56,
              background: at(1) ? "#eff6ff" : "#f1f5f9",
              borderRadius: 8,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <span style={{ color: at(1) ? "#2563eb" : "#94a3b8", display: "flex" }}><IScan size={32} /></span>
          </div>
        </div>
      </button>

      {/* Sync button (step 3) */}
      {at(3) && (
        <button
          onClick={() => onGo(3)}
          style={{
            position: "absolute", left: 736, top: 16,
            width: 46, height: 46, borderRadius: "50%",
            background: "#2563eb", border: "none",
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 16px rgba(37,99,235,0.4)",
            color: "white",
            animation: "bp-spin-slow 4s linear infinite",
          }}
          aria-label="Passport kept up to date"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 11a8 8 0 00-14.5-4M4 4v4h4M4 13a8 8 0 0014.5 4M20 20v-4h-4"/></svg>
        </button>
      )}

      {/* EU Registry */}
      <button
        onClick={() => onGo(2)}
        style={{
          position: "absolute",
          left: 540, top: 330,
          width: 220, height: 64,
          borderRadius: 12,
          background: at(2) ? "white" : "#f1f5f9",
          border: `1.5px solid ${at(2) ? "#bfdbfe" : "#e2e8f0"}`,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "0 14px",
          boxShadow: at(2) ? "0 4px 12px rgba(37,99,235,0.1)" : "none",
          transition: "all 0.4s ease",
        }}
      >
        <div
          style={{
            width: 32, height: 32, borderRadius: 8,
            background: at(2) ? "#eff6ff" : "#f1f5f9",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={at(2) ? "#2563eb" : "#94a3b8"} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-5 9 5M5 9v9M9.5 9v9M14.5 9v9M19 9v9M3 20h18" />
          </svg>
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: at(2) ? "#1e40af" : "#94a3b8" }}>EU Registry</span>
        {at(2) && (
          <span
            style={{
              marginLeft: "auto",
              width: 20, height: 20, borderRadius: "50%",
              background: "#2563eb",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <svg width="10" height="10" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M3 8.5l3 3 7-7" fill="none" stroke="white" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}
      </button>
    </div>
  );
}

function HiwNode({ top, active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        position: "absolute",
        left: 4, top,
        width: 230, height: 66,
        borderRadius: 12,
        background: active ? "white" : "#f8fafc",
        border: `1.5px solid ${active ? "#bfdbfe" : "#e2e8f0"}`,
        padding: "0 14px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 10,
        textAlign: "left",
        boxShadow: active ? "0 2px 12px rgba(37,99,235,0.1)" : "none",
        transition: "all 0.3s ease",
      }}
    >
      <span
        style={{
          width: 34, height: 34, borderRadius: 8,
          background: active ? "#eff6ff" : "#f1f5f9",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: active ? "#2563eb" : "#94a3b8",
          flexShrink: 0,
        }}
      >
        {children[0]}
      </span>
      <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {children[1]}
        {children[2]}
      </span>
    </button>
  );
}

function FadeIn({ children, className = "" }) {
  return <div className={`bp-rv ${className}`}>{children}</div>;
}

/* ── Section 3 - Passport Categories ────────────────────────────────────────── */
function PassportCategoriesSection() {
  const [active, setActive] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const catIcons = [IShield, IChain, IGlobe, IUsers, IBattery, IPlug, ISeal];
  const catColors = ["#2563EB","#1E40AF","#2563EB","#1E40AF","#2563EB","#1E40AF","#2563EB"];

  return (
    <section id="passport-categories" style={{ paddingBlock: "clamp(52px,8vw,96px)", background: "var(--bg)" }}>
      <div className="bp-wrap">
        <div className="bp-head">
          <p className="bp-eyebrow bp-rv">Annex XIII · Complete Data Requirements</p>
          <h2 className="bp-rv bp-d1">Every field, mapped and ready</h2>
          <p className="bp-lede bp-rv bp-d2">
            Annex XIII of EU Regulation 2023/1542 defines exactly which data fields must
            appear in a battery passport. Every category, every field, and who can see it.
          </p>
        </div>

        <div className="bp-legend bp-rv bp-d3" style={{ marginBottom: "24px" }}>
          <span><i style={{ width:10,height:10,borderRadius:3,display:"inline-block",background:"var(--accent)" }}/>Public</span>
          <span><i style={{ width:10,height:10,borderRadius:3,display:"inline-block",background:"var(--amber)" }}/>Repairers &amp; recyclers</span>
          <span><i style={{ width:10,height:10,borderRadius:3,display:"inline-block",background:"var(--violet)" }}/>Authorities only</span>
          <span><i style={{ width:10,height:10,borderRadius:3,display:"inline-block",border:"1.5px dashed var(--good)" }}/>Live data</span>
        </div>

        <div className="bp-cat-grid">
          {(showAll ? SECTIONS : SECTIONS.slice(0, 4)).map(([name, desc, fields], idx) => {
            const Icon = catIcons[idx] || IShield;
            const pubCount  = fields.filter(([,a]) => a === P).length;
            const repCount  = fields.filter(([,a]) => a === R).length;
            const authCount = fields.filter(([,a]) => a === A).length;
            const liveCount = fields.filter(([,a]) => a === L).length;
            const isOpen = active === idx;
            const delay = `${idx * 0.07}s`;
            return (
              <div
                key={idx}
                className={`bp-cat-card${idx < 4 ? " bp-rv" : ""}`}
                style={
                  idx < 4
                    ? { borderTop: `3px solid ${catColors[idx]}`, transitionDelay: delay }
                    : {
                        borderTop: `3px solid ${catColors[idx]}`,
                        animation: `bp-fade-up .55s cubic-bezier(.16,1,.3,1) ${(idx - 4) * 0.12}s backwards`,
                      }
                }
              >
                <div style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                    background: "var(--accent-soft)", display: "grid", placeItems: "center",
                    color: "var(--accent)", transition: "transform .2s"
                  }}>
                    <Icon size={22} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="bp-cat-num">Category {String(idx + 1).padStart(2, "0")}</div>
                    <h3 style={{ fontSize: "1rem", marginTop: 2 }}>{name}</h3>
                    <p style={{ color: "var(--muted)", fontSize: ".87rem", marginTop: 4 }}>{desc}</p>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {pubCount  > 0 && <span style={{ fontFamily:"var(--f-mono)",fontSize:".7rem",padding:"3px 9px",borderRadius:999,background:"var(--accent-soft)",color:"var(--accent)",fontWeight:600 }}>{pubCount} public</span>}
                  {repCount  > 0 && <span style={{ fontFamily:"var(--f-mono)",fontSize:".7rem",padding:"3px 9px",borderRadius:999,background:"var(--amber-soft)",color:"var(--amber)",fontWeight:600 }}>{repCount} restricted</span>}
                  {authCount > 0 && <span style={{ fontFamily:"var(--f-mono)",fontSize:".7rem",padding:"3px 9px",borderRadius:999,background:"var(--violet-soft)",color:"var(--violet)",fontWeight:600 }}>{authCount} authority</span>}
                  {liveCount > 0 && <span style={{ fontFamily:"var(--f-mono)",fontSize:".7rem",padding:"3px 9px",borderRadius:999,background:"var(--good-soft)",color:"var(--good)",fontWeight:600 }}>{liveCount} live</span>}
                </div>

                <button
                  onClick={() => setActive(isOpen ? null : idx)}
                  style={{
                    all: "unset", cursor: "pointer", display: "flex", justifyContent: "space-between",
                    alignItems: "center", fontSize: ".86rem", fontWeight: 700, color: "var(--accent)",
                    fontFamily: "var(--f-sans)", transition: "opacity .15s"
                  }}
                >
                  {isOpen ? "Hide fields" : `View all ${fields.length} fields`}
                  <span style={{ fontSize: "1rem", transition: "transform .25s", transform: isOpen ? "rotate(45deg)" : "rotate(0deg)", display:"inline-block" }}>+</span>
                </button>

                {isOpen && (
                  <div className="bp-cat-fields" style={{ animation: "bp-fade-up .25s ease" }}>
                    {fields.map(([fname, access], fi) => {
                      const cls = { pub: "", rep: "frep", auth: "fauth", live: "flive" }[access] || "";
                      return (
                        <span key={fi} className={`bp-cat-fld ${cls}`}>
                          <ICheck size={11} />
                          {fname}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginTop: 28 }}>
          <button
            type="button"
            className="bp-btn bp-btn-ghost bp-more-btn"
            aria-expanded={showAll}
            onClick={() => setShowAll(v => !v)}
          >
            {showAll ? "View less" : `View ${SECTIONS.length - 4} more categories`}
            <svg
              width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
              style={{ transition: "transform .35s ease", transform: showAll ? "rotate(180deg)" : "rotate(0deg)" }}
            >
              <path d="M4 6l4 4 4-4"/>
            </svg>
          </button>
        </div>

        <div
          className="bp-rv bp-d2"
          style={{
            marginTop: 32, padding: "20px 24px", borderRadius: "var(--r)",
            background: "var(--accent-soft)", border: "1px solid rgba(37,99,235,.3)",
            display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap"
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, color: "var(--accent-strong)", marginBottom: 4 }}>We collect and map every field</div>
            <p style={{ color: "var(--muted)", fontSize: ".92rem", margin: 0 }}>
              Provinyx handles data collection across all 7 categories, chases suppliers for restricted fields,
              and issues the complete passport - registered with the EU registry.
            </p>
          </div>
          <button
            onClick={() => document.getElementById("gap-score")?.scrollIntoView({ behavior: "smooth" })}
            className="bp-btn bp-btn-primary"
            style={{ whiteSpace: "nowrap" }}
          >
            Check your readiness <IArrow />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ── Section 6 - Gap Score (10 questions, 3 per page) ──────────────────────── */
const QUIZ_OPTS = Object.keys(QUIZ_VALS);
const QUIZ_PER_PAGE = 3;

const GAP_STEPS = [
  { n:"1", title:"Gap check",       give:"A 30-min call and one datasheet",              deliver:"Your score and a roadmap" },
  { n:"2", title:"Data collection", give:"Specs, BOM, test reports, supplier contacts",  deliver:"Data mapped to Annex XIII, suppliers chased" },
  { n:"3", title:"Identity set-up", give:"Company details and sign-off",                 deliver:"QSeal, unique IDs and QR labels" },
  { n:"4", title:"Go live",         give:"Final approval",                               deliver:"Passport published and registered" },
  { n:"5", title:"Stay compliant",  give:"Updates when something changes",               deliver:"Passports kept current" },
];

function GapScoreSection({ onContact }) {
  const total = QUIZ_QS.length;
  const [answers, setAnswers] = useState(() => Array(total).fill(null));
  const [page, setPage] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [meterOn, setMeterOn] = useState(false);

  const pages = Math.ceil(total / QUIZ_PER_PAGE);
  const start = page * QUIZ_PER_PAGE;
  const end = Math.min(start + QUIZ_PER_PAGE, total);
  const answered = answers.filter(a => a !== null).length;
  const allDone = answered === total;
  const pageDone = answers.slice(start, end).every(a => a !== null);
  const score = answers.reduce((s, v) => s + (v || 0), 0);
  const scoreLabel = Number.isInteger(score) ? String(score) : score.toFixed(1);
  const gaps = QUIZ_QS.map((q, i) => ({ q, v: answers[i] })).filter(x => x.v !== null && x.v < 1);

  const band =
    score >= 8
      ? { label: "Well prepared", text: "You're in good shape. A short gap check will close the last few items." }
      : score >= 5
      ? { label: "On your way",   text: "You have a solid base, but several areas still need work before the deadline." }
      : { label: "Early stage",   text: "There's a fair amount to do, and starting now leaves enough time to get there." };

  useEffect(() => {
    if (!showResult) { setMeterOn(false); return; }
    const t = setTimeout(() => setMeterOn(true), 80);
    return () => clearTimeout(t);
  }, [showResult]);

  const pick = (qi, key) =>
    setAnswers(prev => { const a = [...prev]; a[qi] = QUIZ_VALS[key]; return a; });

  const retake = () => {
    setAnswers(Array(total).fill(null));
    setPage(0);
    setShowResult(false);
  };

  return (
    <section className="bp-band" id="gap-score">
      <div
        className="bp-wrap bp-gap-wrap"
        style={showResult ? undefined : { gridTemplateColumns: "minmax(0, 720px)", justifyContent: "center" }}
      >
        <div>
          <div className="bp-head" style={{ marginBottom: 20 }}>
            <p className="bp-eyebrow bp-rv">Your Gap Score</p>
            <h2 className="bp-rv bp-d1">How ready are you?</h2>
            <p className="bp-lede bp-rv bp-d2">
              Ten questions on your data, identity and supply chain. You get a score out of
              ten and a plain list of what's missing.
            </p>
          </div>

          <div className="bp-card bp-quiz bp-rv bp-d3">
            {!showResult ? (
              <>
                <div className="bp-score-now">
                  <span>Questions {start + 1}–{end} of {total}</span>
                  <span><b>{answered}</b> / {total} answered</span>
                </div>
                <div className="bp-meter" aria-hidden="true">
                  <i style={{ width: `${(answered / total) * 100}%` }}/>
                </div>

                <div key={page} className="bp-q-page" style={{ display: "grid", gap: 14 }}>
                  {QUIZ_QS.slice(start, end).map((q, i) => {
                    const qi = start + i;
                    return (
                      <div key={qi} className="bp-q">
                        <p>{qi + 1}. {q}</p>
                        <div className="opts">
                          {QUIZ_OPTS.map(key => (
                            <button
                              key={key} type="button"
                              className={`bp-opt${answers[qi] === QUIZ_VALS[key] ? " sel" : ""}`}
                              onClick={() => pick(qi, key)}
                            >
                              {key}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bp-quiz-nav">
                  <button
                    type="button" className="bp-btn bp-btn-ghost bp-btn-sm"
                    disabled={page === 0} onClick={() => setPage(p => p - 1)}
                  >
                    Back
                  </button>
                  <span className="bp-small" style={{ alignSelf: "center" }}>Page {page + 1} of {pages}</span>
                  {page < pages - 1 ? (
                    <button
                      type="button" className="bp-btn bp-btn-ghost bp-btn-sm"
                      disabled={!pageDone} onClick={() => setPage(p => p + 1)}
                    >
                      Next <IArrow size={14}/>
                    </button>
                  ) : <span/>}
                </div>

                <div style={{ display: "grid", gap: 8, justifyItems: "center", paddingTop: 14, borderTop: "1px solid var(--line)" }}>
                  <button
                    type="button" className="bp-btn bp-btn-primary"
                    disabled={!allDone} onClick={() => setShowResult(true)}
                  >
                    Find your gap score <IArrow/>
                  </button>
                  <span className="bp-small">
                    {allDone ? "All set. See how you scored." : `Answer all ${total} questions to unlock · ${total - answered} left`}
                  </span>
                </div>
              </>
            ) : (
              <div className="bp-q-page" style={{ display: "grid", gap: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <span className="bp-eyebrow">Your gap score</span>
                  <span style={{ fontSize: ".78rem", fontWeight: 700, padding: "4px 12px", borderRadius: 999, background: "var(--accent-soft)", color: "var(--accent-strong)" }}>
                    {band.label}
                  </span>
                </div>

                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontSize: "clamp(2.8rem,6vw,3.8rem)", fontWeight: 800, lineHeight: 1, letterSpacing: "-.03em", fontVariantNumeric: "tabular-nums" }}>
                    {scoreLabel}
                  </span>
                  <span className="bp-small" style={{ fontSize: "1.1rem" }}>/ {total}</span>
                </div>

                <div className="bp-meter">
                  <i style={{ width: meterOn ? `${(score / total) * 100}%` : "0%", transition: "width 1s cubic-bezier(.16,1,.3,1)" }}/>
                </div>
                <p style={{ color: "var(--muted)", fontSize: ".95rem" }}>{band.text}</p>

                {gaps.length > 0 ? (
                  <div style={{ display: "grid", gap: 8 }}>
                    <b style={{ fontSize: ".9rem" }}>What's missing</b>
                    <div className="bp-gap-list">
                      {gaps.map(({ q, v }) => (
                        <div key={q} className="bp-gap-item">
                          <span>{q}</span>
                          <span
                            className="bp-gap-tag"
                            style={v === 0
                              ? { background: "#FEE4E2", color: "#B42318" }
                              : { background: "var(--amber-soft)", color: "var(--amber)" }}
                          >
                            {v === 0 ? "Not yet" : "Partly"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bp-done-msg">You answered Yes to everything. Nothing is missing.</div>
                )}

                <div className="bp-btns">
                  <button type="button" className="bp-btn bp-btn-primary" onClick={onContact}>
                    Talk to an expert <IArrow/>
                  </button>
                  <button type="button" className="bp-btn bp-btn-ghost" onClick={retake}>
                    Retake
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {showResult && (
          <div style={{ animation: "bp-slide-right .7s cubic-bezier(.16,1,.3,1) .15s both" }}>
            <p className="bp-eyebrow" style={{ color: "var(--muted)", marginBottom: 16 }}>Our five steps</p>
            <div className="bp-steps">
              {GAP_STEPS.map((s, i) => (
                <div
                  key={i}
                  className="bp-step"
                  style={{ animation: `bp-slide-right .55s cubic-bezier(.16,1,.3,1) ${0.3 + i * 0.1}s both` }}
                >
                  <span className="k">{s.n}</span>
                  <b>{s.title}</b>
                  <div className="io">
                    <span><em>You give</em>{s.give}</span>
                    <span><em>We deliver</em>{s.deliver}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Section 8 - Why Partner With Us ────────────────────────────────────────── */
/* ── Section 8 - Why Partner With Us ────────────────────────────────────────── */
const WHY_TASKS = [
  "Data collection and mapping",
  "Supplier follow-up",
  "Passport creation",
  "QSeal set-up",
  "EU registry registration",
  "Ongoing updates",
];

const WHY_STARTS = [
  { t: "Free gap check",     d: "Know exactly where you stand in two minutes" },
  { t: "No IT team needed",  d: "We work with the data you already have" },
  { t: "Clear pricing",      d: "Transparent plans, no surprise enterprise contracts" },
];

function WhyPartnerSection() {
  const [done, setDone] = useState(() => Array(WHY_TASKS.length).fill(false));
  const panelRef = useRef(null);
  const timers = useRef([]);
  const count = done.filter(Boolean).length;
  const all = count === WHY_TASKS.length;

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  // Lifts the six tasks off the plate one after another
  const takeAll = () => {
    clearTimers();
    setDone(Array(WHY_TASKS.length).fill(false));
    WHY_TASKS.forEach((_, i) => {
      timers.current.push(
        setTimeout(() => setDone(d => { const n = [...d]; n[i] = true; return n; }), 350 + i * 450)
      );
    });
  };

  const reset = () => { clearTimers(); setDone(Array(WHY_TASKS.length).fill(false)); };

  const toggle = i => {
    clearTimers();
    setDone(d => { const n = [...d]; n[i] = !n[i]; return n; });
  };

  // Plays once when the section scrolls into view
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const ob = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { takeAll(); ob.disconnect(); }
    }, { threshold: 0.35 });
    ob.observe(el);
    return () => { ob.disconnect(); clearTimers(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="bp-why" id="why-partner">
      <div className="bp-wrap">

        {/* Row 1: message (left) + interactive plate (right) */}
        <div className="bp-why-split">
          <div className="bp-head" style={{ maxWidth: 560 }}>
            <p className="bp-eyebrow bp-rv" style={{ color: "#8FB2FF" }}>Why partner with us</p>
            <h2 className="bp-rv bp-d1" style={{ color: "var(--deep-ink)" }}>
              You build the batteries.<br/>We handle the passport.
            </h2>
            <div className="bp-rv bp-d2">
              <p className="bp-wp-sub">Why smaller companies get left behind</p>
              <p style={{ color: "var(--deep-muted)", fontSize: "1.02rem" }}>
                Large manufacturers have compliance teams, consortium memberships and budgets
                for big software projects. Most small and mid-sized companies have a quality
                lead and a deadline. The rules are the same; the resources are not.
              </p>
            </div>
          </div>

          <div className="bp-wp-panel bp-rv-r bp-d2" ref={panelRef}>
            <div className="bp-wp-top">
              <b>What we take off your plate</b>
              <span className="bp-wp-count">{count} / {WHY_TASKS.length} handled</span>
            </div>
            <div className="bp-wp-bar" aria-hidden="true">
              <i style={{ width: `${(count / WHY_TASKS.length) * 100}%` }}/>
            </div>

            <ul className="bp-wp-tasks">
              {WHY_TASKS.map((task, i) => (
                <li key={task}>
                  <button
                    type="button"
                    className={`bp-wp-task${done[i] ? " done" : ""}`}
                    aria-pressed={done[i]}
                    onClick={() => toggle(i)}
                  >
                    <span className="bp-wp-check"><ICheck size={13}/></span>
                    <span className="bp-wp-label">{task}</span>
                  </button>
                </li>
              ))}
            </ul>

            <div className="bp-wp-actions">
              <button type="button" className="bp-wp-btn" onClick={all ? reset : takeAll}>
                {all ? "Put it back" : "Take it all off"}
              </button>
              <span className="bp-small" style={{ color: "var(--deep-muted)" }}>
                {all ? "All of it is on us." : "Click a task to hand it over."}
              </span>
            </div>
          </div>
        </div>

        {/* Row 2: getting started + CTA */}
        <div className="bp-wp-row2 bp-rv bp-d2">
          <div>
            <p className="bp-wp-sub">Getting started is easy</p>
            <div className="bp-wp-steps">
              {WHY_STARTS.map((s, i) => (
                <div key={s.t} className="bp-wp-step">
                  <span className="bp-wp-no">0{i + 1}</span>
                  <b>{s.t}</b>
                  <span>{s.d}</span>
                </div>
              ))}
            </div>
          </div>

          {/* <button
            onClick={() => document.getElementById("gap-score")?.scrollIntoView({ behavior: "smooth" })}
            className="bp-why-btn bp-why-btn-white"
            style={{ flexShrink: 0 }}
          >
            Book a free gap check <IArrow/>
          </button> */}
        </div>
      </div>
    </section>
  );
}

/* ── Main page ───────────────────────────────────────────────────────────────── */
export default function BatteryPassportPage() {
  usePageStyles();
  useReveal();

  const [days,      setDays]      = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [hbEmail,   setHbEmail]   = useState("");
  const [hbDone,    setHbDone]    = useState(false);
  const [hbSending, setHbSending] = useState(false);
  const [hbError,   setHbError]   = useState("");
  const [cf,        setCf]        = useState({ name:"",email:"",company:"",role:"",type:"EV",msg:"" });
  const [cfDone,    setCfDone]    = useState(false);
  const [cfSending, setCfSending] = useState(false);
  const [showSample, setShowSample] = useState(false);
  const [cfError,   setCfError]   = useState("");


  useEffect(() => {
    const d = Math.max(0, Math.ceil((new Date("2027-02-18T00:00:00") - new Date()) / 864e5));
    setDays(d);
  }, []);

  const hbValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(hbEmail.trim());
  const sendHandbook = async () => {
    if (!hbValid || hbSending) return;
    setHbSending(true); setHbError("");
    try {
      // The Apps Script emails the SME handbook (.docx) to this address and logs the lead.
      await submitToSheet({ type: "Handbook", email: hbEmail.trim(), source: "Battery Passport page" });
      setHbDone(true);
    } catch {
      setHbError("Couldn't send the handbook right now. Please try again.");
    } finally {
      setHbSending(false);
    }
  };
  const updateCf = (k, v) => setCf(p => ({ ...p, [k]: v }));
  const cfValid = cf.name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(cf.email.trim());
  const sendExpert = async () => {
    if (cfSending) return;
    if (!cfValid) { setCfError("Please enter your name and a valid email."); return; }
    setCfSending(true); setCfError("");
    try {
      await submitToSheet({
        type: "Talk to Expert",
        name: cf.name.trim(),
        email: cf.email.trim(),
        company: cf.company.trim(),
        role: cf.role.trim(),
        batteryType: cf.type,
        message: cf.msg.trim(),
        source: "Battery Passport page",
      });
      setCfDone(true);
    } catch {
      setCfError("Something went wrong. Please try again.");
    } finally {
      setCfSending(false);
    }
  };
  const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bp-page">
      {showSample && <SamplePassport onClose={() => setShowSample(false)} />}

      {/* ── 1. Hero ─────────────────────────────────────────────────────────── */}
      <section className="bp-hero" id="battery-passport">
        {/* Animated background blobs */}
        <div className="bp-hero-bg">
          <div className="bp-blob1"/>
          <div className="bp-blob2"/>
        </div>

        <div className="bp-wrap bp-hero-grid">
          {/* Left copy - slide in from left */}
          <div className="bp-hero-copy bp-rv-l">
            <div className="bp-hero-badge">
              <IBattery size={14}/>
              EU Battery Regulation 2023/1542
            </div>
            <h1>Your battery passport, sorted before the deadline</h1>
            <p className="bp-lede">
              We check your gaps, collect the data, issue the passport and register it, so
              your team can keep building batteries.
            </p>
            <div className="bp-btns">
              <button className="bp-btn bp-btn-primary" onClick={() => scrollTo("gap-score")}>
                Check your readiness <IArrow/>
              </button>
              <button className="bp-btn bp-btn-ghost" onClick={() => setShowSample(true)}>
                See sample passport
              </button>
            </div>
            <div className="bp-trust">
              <span><ICheck size={14}/>Annex XIII compliant</span>
              <span><ICheck size={14}/>EU registry submission</span>
              <span><ICheck size={14}/>eIDAS QSeal included</span>
            </div>
          </div>

          {/* Right visual - live dashboard animation (plays continuously) */}
          <div className="bp-hero-visual bp-rv-r">
            <div className="glow"/>
            <div style={{ width: "100%", position: "relative", zIndex: 1 }}>
              <DashboardMockup embedded />
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bp-wrap">
          <div className="bp-timeline" aria-label="Key dates">
            <div className="bp-tl past bp-rv bp-d1">
              <span className="d">17 Aug 2023</span>
              <b>Regulation in force</b>
            </div>
            <div className="bp-tl past bp-rv bp-d2">
              <span className="d">19 Jul 2026</span>
              <b>EU DPP registry</b>
              <span className="verify">Confirm current status</span>
            </div>
            <div className="bp-tl key bp-rv bp-d3">
              <span className="d">18 Feb 2027</span>
              <b>Passport mandatory</b>
              <span className="bp-small"><strong>{days}</strong> days from today</span>
            </div>
          </div>
          <p className="bp-small bp-rv" style={{ marginTop: 18 }}>
            Article 77 requires the passport. Annex XIII lists what goes in it.
          </p>
        </div>
      </section>

      {/* ── 2. Contents ─────────────────────────────────────────────────────── */}
      {/* <section className="bp-band" id="contents">
        <div className="bp-wrap">
          <div className="bp-head">
            <p className="bp-eyebrow bp-rv">What goes in the passport</p>
            <h2 className="bp-rv bp-d1">Seven sections, one passport</h2>
          </div>

          <div className="bp-legend bp-rv bp-d2" aria-label="Who can see each field">
            <span><i style={{ width:10,height:10,borderRadius:3,display:"inline-block",background:"var(--accent)" }}/> Public</span>
            <span><i style={{ width:10,height:10,borderRadius:3,display:"inline-block",background:"var(--amber)" }}/> Repairers &amp; recyclers</span>
            <span><i style={{ width:10,height:10,borderRadius:3,display:"inline-block",background:"var(--violet)" }}/> Authorities only</span>
            <span><i style={{ width:10,height:10,borderRadius:3,display:"inline-block",border:"1.5px dashed var(--good)" }}/> Live data</span>
          </div>

          <div className="bp-card bp-contents bp-rv bp-d3">
            <div className="bp-ctabs" role="tablist">
              {SECTIONS.map(([name], i) => (
                <button
                  key={i}
                  role="tab"
                  aria-selected={activeTab === i}
                  className={`bp-ctab${activeTab === i ? " active" : ""}`}
                  onClick={() => setActiveTab(i)}
                >
                  <span className="n">{String(i+1).padStart(2,"0")}</span>
                  {name}
                </button>
              ))}
            </div>
            <div className="bp-cpanel" role="tabpanel">
              <h3 style={{ fontSize: "1.3rem" }}>{SECTIONS[activeTab][0]}</h3>
              <p>{SECTIONS[activeTab][1]}</p>
              <div className="bp-chips">
                {SECTIONS[activeTab][2].map(([fname, access], fi) => (
                  <span key={fi} className={`bp-field ${FIELD_CLS[access]}`}>
                    <i style={{ background: FIELD_COLOR[access], width:8,height:8,borderRadius:2,display:"inline-block",flexShrink:0 }}/>
                    {fname}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* ── 3. Passport Categories ──────────────────────────────────────────── */}
      <PassportCategoriesSection/>

      {/* ── 4. Scope ─────────────────────────────────────────────────────────── */}
      <section className="bp-band" id="scope">
        <div className="bp-wrap">
          <div className="bp-head">
            <p className="bp-eyebrow bp-rv">Is your battery in scope?</p>
            <h2 className="bp-rv bp-d1">Which batteries need a passport</h2>
          </div>

          <div className="bp-scope">
            {[
              { Icon: ICar,     title: "Electric-vehicle batteries", desc: "Cars, vans, trucks and buses" },
              { Icon: IBike,    title: "LMT batteries",              desc: "E-bikes, e-scooters and other light vehicles" },
              { Icon: IFactory, title: "Industrial above 2 kWh",     desc: "Stationary storage (BESS), forklifts and more, any chemistry" },
            ].map(({ Icon, title, desc }, i) => (
              <div key={i} className={`bp-card bp-rv bp-d${i+1}`} style={{ padding: 22, display: "grid", gap: 10, alignContent: "start" }}>
                <Icon/><h3>{title}</h3>
                <span style={{ color:"var(--muted)",fontSize:".92rem" }}>{desc}</span>
              </div>
            ))}
          </div>

          <div className="bp-scope-foot">
            <div className="bp-card bp-rv" style={{ padding: "20px 22px", display: "grid", gap: 10 }}>
              <b style={{ fontWeight:700 }}>Not in scope</b>
              <div className="bp-out">
                <IX size={16}/>
                <span className="bp-small">Portable batteries (phones, laptops, tools) and starter (SLI) batteries</span>
              </div>
            </div>
            <div className="bp-card bp-rv bp-d1" style={{ padding: "20px 22px", display: "grid", gap: 10 }}>
              <b style={{ fontWeight:700 }}>Who is responsible</b>
              <div className="bp-roles">
                <div><b>Manufacturer</b><span>When it sells the battery in the EU itself</span></div>
                <div><b>Importer</b><span>When the maker is outside the EU and doesn't provide the passport</span></div>
                <div><b>Distributor</b><span>When it sells under its own name or modifies the battery</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. How it works ───────────────────────────────────────────────── */}
      <HowItWorksSection/>


      {/* ── 6. Gap Score ── */}
      <GapScoreSection onContact={() => scrollTo("contact")}/>

      {/* ── 7. Handbook ── */}
      <section id="handbook" style={{ paddingBlock: "clamp(32px,5vw,64px)" }}>
        <div className="bp-wrap">
          <div className="bp-card bp-handbook bp-rv">
            <span className="bp-hb-orb o1" aria-hidden="true"/>
            <span className="bp-hb-orb o2" aria-hidden="true"/>
            <span className="bp-hb-shine" aria-hidden="true"/>

            <div className="bp-book-wrap" aria-hidden="true">
              <div className="bp-book"><span>Battery<br/>Passport<br/>Handbook</span></div>
              <i className="bp-book-shadow"/>
            </div>

            <div className="bp-hb-copy">
              <p className="bp-eyebrow bp-hb-live"><i/>E-Handbook</p>
              <h3>The SME Battery Passport Handbook</h3>
              <p className="bp-small">
                A practical guide to Annex XIII with a prioritised checklist for lean teams,
                updated as the rules evolve.
              </p>
              <div className="bp-hb-chips">
                {["Annex XIII explained", "Prioritised SME checklist", "Updated as rules evolve"].map(c => (
                  <span key={c}><ICheck size={13}/>{c}</span>
                ))}
              </div>
            </div>

            {hbDone ? (
              <div className="bp-done-msg">Thanks - the handbook is on its way to your inbox. Check your spam folder if you don't see it.</div>
            ) : (
              <div className="bp-hb-form">
                <input
                  className="bp-input"
                  type="email"
                  placeholder="Your work email"
                  value={hbEmail}
                  onChange={e => { setHbEmail(e.target.value); setHbError(""); }}
                  onKeyDown={e => e.key === "Enter" && sendHandbook()}
                />
                <button
                  className="bp-btn bp-btn-primary"
                  disabled={!hbValid || hbSending}
                  onClick={sendHandbook}
                >
                  {hbSending ? "Sending…" : "Get the guide"}
                </button>
                {hbError && <div className="bp-hb-err" role="alert">{hbError}</div>}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── 8. FAQ ───────────────────────────────────────────────────────────── */}
      <section className="bp-band" id="faq">
        <div className="bp-wrap">
          <div className="bp-head">
            <p className="bp-eyebrow bp-rv">FAQ</p>
            <h2 className="bp-rv bp-d1">Common questions</h2>
          </div>

          <div className="bp-faq-wrap">
            <div className="bp-faq bp-rv bp-d2">
              {[
                ["Which batteries must have a passport?", "EV batteries, industrial batteries above 2 kWh, and LMT (e-bike / e-scooter) batteries placed on the EU market from 18 Feb 2027."],
                ["What is a unique battery identifier?", "A string that uniquely identifies the battery - encoded in a QR or data matrix label on the housing - and links to the passport in the EU registry."],
                ["What is an eIDAS Qualified Electronic Seal?", "A digital seal that lets an organisation sign the passport with the same legal effect as a handwritten signature, as required by the EU Battery Regulation."],
                ["What is the EU battery registry?", "The central database operated by the EU that will store and serve every battery passport. Issuers must register each passport before placing the battery on the market."],
                ["What is a carbon-footprint performance class?", "A letter grade (A–E) that classifies a battery's lifecycle CO₂ footprint relative to a threshold set by the Commission. From February 2027, the class must appear on the passport."],
                ["What does 'State of Health' mean?", "SoH is the ratio of a battery's current energy capacity to its rated capacity. It starts at 100% and falls as the battery ages - the passport must display a live SoH."],
              ].map(([q, a], i) => (
                <details key={i}>
                  <summary>{q}</summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>

            <div id="contact">
              <div className="bp-card bp-contact bp-rv bp-d3">
                <h3 style={{ fontSize:"1.2rem" }}>Talk to an expert</h3>
                {cfDone ? (
                  <div className="bp-done-msg">
                    Thanks, {cf.name.split(" ")[0]}! We'll be in touch.
                  </div>
                ) : (
                  <>
                    <div className="row">
                      <label>Name<input placeholder="Your name" value={cf.name} onChange={e => updateCf("name", e.target.value)}/></label>
                      <label>Email<input type="email" placeholder="you@company.com" value={cf.email} onChange={e => updateCf("email", e.target.value)}/></label>
                    </div>
                    <div className="row">
                      <label>Company<input placeholder="Company name" value={cf.company} onChange={e => updateCf("company", e.target.value)}/></label>
                      <label>Your role<input placeholder="e.g. Compliance lead" value={cf.role} onChange={e => updateCf("role", e.target.value)}/></label>
                    </div>
                    <label>Battery type
                      <select value={cf.type} onChange={e => updateCf("type", e.target.value)}>
                        <option>EV</option><option>Industrial</option><option>LMT</option><option>Multiple</option>
                      </select>
                    </label>
                    <label>Message (optional)
                      <textarea placeholder="Tell us about your situation…" value={cf.msg} onChange={e => updateCf("msg", e.target.value)}/>
                    </label>
                    {cfError && <div className="bp-hb-err" role="alert">{cfError}</div>}
                    <button
                      className="bp-btn bp-btn-primary"
                      style={{ width:"100%", justifyContent:"center" }}
                      disabled={cfSending}
                      onClick={sendExpert}
                    >
                      {cfSending ? "Sending…" : <>Send message <IArrow/></>}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 9. Why Partner ───────────────────────────────────────────────────── */}
      <WhyPartnerSection/>

      {/* ── 10. Footer ───────────────────────────────────────────────────────── */}
      <Footer/>

    </div>
  );
}